<template>
    <div class="step-5 d-flex">
        <!-- Session notification banner (errors, waiting status) -->
        <v-snackbar
            v-model="sessionNotification.show"
            :color="sessionNotification.type === 'error' ? 'error' : sessionNotification.type === 'success' ? 'success' : sessionNotification.type === 'warning' ? 'warning' : 'info'"
            :timeout="sessionNotification.type === 'error' || sessionNotification.type === 'warning' ? 10000 : 5000"
            app
            top
            centered
            content-class="session-notification-snackbar">
            {{ sessionNotification.text }}
            <template v-slot:action="{ attrs }">
                <v-btn
                    v-bind="attrs"
                    text
                    @click="sessionNotification.show = false">
                    Close
                </v-btn>
            </template>
        </v-snackbar>
        <!-- Mobile menu button -->
        <v-btn
            v-if="!leftMenuOpen && isTabletOrPhone"
            class="mobile-menu-toggle ui-no-zoom"
            icon
            @click="leftMenuOpen = true">
            <v-icon>mdi-menu</v-icon>
        </v-btn>
        
        <!-- Overlay for mobile -->
        <div 
            v-if="leftMenuOpen && isPhone"
            class="mobile-overlay"
            @click="leftMenuOpen = false">
        </div>
        
        <div class="left-wrapper" :class="{ 'mobile-open': leftMenuOpen || !isTabletOrPhone, 'mobile-drawer': isTabletOrPhone }">
            <div class="left ui-no-zoom d-flex flex-column pa-2">
            <div class="left-scroll d-flex flex-column">
            <!-- Open in app button for monocular sessions on mobile -->
            <v-btn
                v-if="showOpenInAppButton"
                color="primary-dark"
                class="mb-4 w-100"
                large
                @click="openInApp">
                <v-icon left>mdi-cellphone-arrow-down</v-icon>
                Open in App
            </v-btn>
            <!-- Monocular beta notification banner -->
            <v-alert
                v-if="isMonocularSession"
                type="info"
                dense
                outlined
                :icon="false"
                class="monocular-beta-alert mb-4"
                border="left">
              <div class="monocular-beta-content">
                <strong>Monocular mode is in beta.</strong><br>
                <a href="https://www.opencap.ai/best-practices?variant=Monocular" target="_blank" rel="noopener noreferrer">Best practices</a>
                ·
                <a href="https://www.opencap.ai/get-started?variant=monocular" target="_blank" rel="noopener noreferrer">Get started</a>.<br>
                Jumping is not supported yet. Camera should be static at 30-45° angle. Keep the full body in frame, clearly visible (not blocked by objects or people), within 5 meters of the camera.
              </div>
            </v-alert>
  
            <ValidationObserver tag="div" class="d-flex flex-column" ref="observer" v-slot="{ invalid }">
  
                <div v-if="participantName" class="participant-context mb-3">
                  <span class="participant-context__label">Participant:</span>
                  <span class="participant-context__name" :title="participantName">{{ participantName }}</span>
                </div>

                <div class="d-flex align-center flex-wrap mb-2 trial-name-row">
                  <div class="flex-grow-1 min-width-0">
                    <ValidationProvider rules="required|alpha_dash_custom" v-slot="{ errors }" name="Trial name">
                      <v-text-field v-show="show_controls && !showOpenInAppButton" v-model="trialName" label="Trial name" class="flex-grow-0"
                          :disabled="state !== 'ready'" :error="errors.length > 0" :error-messages="errors[0]"
                          autocomplete="off" />
                    </ValidationProvider>
                  </div>
                </div>
  
                  <v-btn class="mb-4 w-100" v-show="show_controls && !showOpenInAppButton" :disabled="recordingActionInFlight || recordingStopCooldownActive || lidarCooldownActive || (state === 'ready' && (busy || invalid))" @click="changeState">
                      {{ buttonCaption }}
                  </v-btn>
                  <p v-if="state === 'recording' && n_cameras_connected >= n_calibrated_cameras">
                    {{ recordingStatusText }}
                    <template v-if="recordingDisplayFramerate"> at {{ recordingDisplayFramerate }} Hz</template><template v-if="isLidarRecordingEnabled">, {{ lidarRecordingStatusText }}</template>,
                    do not refresh
                  </p>
                  <p v-if="state === 'processing'">{{ processingProgressText }}</p>
              </ValidationObserver>

              <div class="show-removed-trials-sidebar mb-2 d-flex align-center">
                <v-checkbox v-model="show_trashed" label="Show removed trials" hide-details dense class="toolbar-checkbox"></v-checkbox>
                <v-spacer></v-spacer>
                <div class="trial-legend-wrap">
                  <v-btn
                    icon
                    small
                    aria-label="Trial color legend"
                    aria-haspopup="true"
                    :aria-expanded="String(trialLegendOpen)"
                    @click.stop="toggleTrialLegend">
                    <v-icon small>mdi-palette-outline</v-icon>
                  </v-btn>
                  <div
                    v-show="trialLegendOpen"
                    class="trial-legend"
                    role="dialog"
                    aria-label="Trial status"
                    @click.stop>
                    <div class="trial-legend__header">
                      <div class="trial-legend__title">Trial status</div>
                      <v-btn
                        icon
                        x-small
                        class="trial-legend__close"
                        aria-label="Close trial status legend"
                        @click.stop="closeTrialLegend">
                        <v-icon x-small>mdi-close</v-icon>
                      </v-btn>
                    </div>
                    <span class="trial-legend__item">
                      <span class="trial-legend__dot trial-legend__dot--done"></span>Done
                    </span>
                    <span class="trial-legend__item">
                      <span class="trial-legend__dot trial-legend__dot--processing"></span>Processing
                    </span>
                    <span class="trial-legend__item">
                      <span class="trial-legend__dot trial-legend__dot--error"></span>Error
                    </span>
                    <span class="trial-legend__item">
                      <span class="trial-legend__dot trial-legend__dot--local"></span>Saved on phone
                    </span>
                  </div>
                </div>
              </div>

              <div class="trials-wrapper flex-grow-1">
                  <div class="trials">
                  <div v-for="(t, index) in filteredTrialsWithMenu"
                      v-bind:item="t"
                      v-bind:index="index"
                      v-bind:key="t.id"
                      class="trial-row">
                      <div :ref="t.id"
                          class="my-1 trial d-flex justify-content-between"
                          :class="{ selected: isSelected(t) }">
                          <Status :value="t" :class="[trialClasses(t), 'flex-grow-1']" @click="loadTrial(t)" />
                          <div class="">
                            <!-- Mobile: bottom sheet for better touch UX -->
                            <template v-if="$vuetify.breakpoint.smAndDown">
                              <v-btn
                                icon
                                @click="openTrialMenuSheet(t)">
                                <v-icon>mdi-menu</v-icon>
                              </v-btn>
                            </template>
                            <!-- Desktop: dropdown menu -->
                            <v-menu
                              v-else
                              v-model="t.isMenuOpen"
                              offset-y
                              right
                              close-on-content-click
                              content-class="trial-context-menu">
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  v-bind="attrs"
                                  v-on="on">
                                  <v-icon>mdi-menu</v-icon>
                                </v-btn>
                              </template>
                              <v-list>
                                <v-list-item link v-if="t.name !== 'neutral'" @click="closeMenuAndRename(t)">
                                  <v-list-item-title>Rename</v-list-item-title>
                                </v-list-item>
                                <v-list-item link v-if="!t.trashed && t.name !== 'neutral'" @click="closeMenuAndAnalysis(t)">
                                  <v-list-item-title>Analysis</v-list-item-title>
                                </v-list-item>
                                <v-list-item link @click="closeMenuAndEditTags(t)">
                                  <v-list-item-title>Edit Tags</v-list-item-title>
                                </v-list-item>
                                <v-list-item link v-if="!t.trashed" @click="closeMenuAndOpenTrashDialog(t)">
                                  <v-list-item-title>Trash</v-list-item-title>
                                </v-list-item>
                                <v-list-item link v-if="t.trashed" @click="closeMenuAndOpenRestoreDialog(t)">
                                  <v-list-item-title>Restore</v-list-item-title>
                                </v-list-item>
                                <v-list-item link @click="closeMenuAndOpenDeleteDialog(t)">
                                  <v-list-item-title>Delete permanently</v-list-item-title>
                                </v-list-item>
                              </v-list>
                            </v-menu>
                          </div>
                      </div>
                      <v-divider v-if="index < filteredTrialsWithMenu.length - 1" class="mx-0 my-1" />
                  </div>
                  </div>
              </div>

              <!-- Mobile: trial options bottom sheet -->
              <v-bottom-sheet
                content-class="bottom-sheet-rounded"
                v-model="showTrialMenuSheet"
                @input="val => !val && (selectedTrialForMenu = null)">
                <v-sheet class="text-center trial-menu-sheet">
                  <v-list v-if="selectedTrialForMenu">
                    <v-list-item link v-if="selectedTrialForMenu.name !== 'neutral'" @click="closeSheetAndRename(selectedTrialForMenu)">
                      <v-list-item-content>
                        <div class="d-flex flex-row align-center justify-center">
                          <v-icon class="mr-3">mdi-pencil</v-icon>
                          <span>Rename</span>
                        </div>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider v-if="selectedTrialForMenu.name !== 'neutral'"></v-divider>
                    <v-list-item link v-if="!selectedTrialForMenu.trashed && selectedTrialForMenu.name !== 'neutral'" @click="closeSheetAndAnalysis(selectedTrialForMenu)">
                      <v-list-item-content>
                        <div class="d-flex flex-row align-center justify-center">
                          <v-icon class="mr-3">mdi-chart-box</v-icon>
                          <span>Analysis</span>
                        </div>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item link @click="closeSheetAndEditTags(selectedTrialForMenu)">
                      <v-list-item-content>
                        <div class="d-flex flex-row align-center justify-center">
                          <v-icon class="mr-3">mdi-tag-multiple</v-icon>
                          <span>Edit Tags</span>
                        </div>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider v-if="!selectedTrialForMenu.trashed"></v-divider>
                    <v-list-item link v-if="!selectedTrialForMenu.trashed" @click="closeSheetAndOpenTrashDialog(selectedTrialForMenu)">
                      <v-list-item-content>
                        <div class="d-flex flex-row align-center justify-center">
                          <v-icon class="mr-3">mdi-delete-outline</v-icon>
                          <span>Trash</span>
                        </div>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider v-if="selectedTrialForMenu.trashed"></v-divider>
                    <v-list-item link v-if="selectedTrialForMenu.trashed" @click="closeSheetAndOpenRestoreDialog(selectedTrialForMenu)">
                      <v-list-item-content>
                        <div class="d-flex flex-row align-center justify-center">
                          <v-icon class="mr-3">mdi-restore</v-icon>
                          <span>Restore</span>
                        </div>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item link @click="closeSheetAndOpenDeleteDialog(selectedTrialForMenu)">
                      <v-list-item-content>
                        <div class="d-flex flex-row align-center justify-center">
                          <v-icon class="mr-3">mdi-delete-forever</v-icon>
                          <span>Delete permanently</span>
                        </div>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-sheet>
              </v-bottom-sheet>

            </div><!-- end left-scroll -->

            <v-btn class="session-actions-toggle w-100" @click="toggleSessionMenuButtons()">
                <v-icon v-if="showSessionMenuButtons">mdi-menu-down</v-icon>
                <v-icon v-else>mdi-menu-up</v-icon>
            </v-btn>

            <div v-if="showSessionMenuButtons" class="session-actions-panel">
                  <v-btn small class="w-100 session-action-btn" v-show="show_controls" :disabled="busy || state !== 'ready'"
                      @click="openNewSessionSameSetupConfirm">
                      <v-icon left small>mdi-plus-box-multiple</v-icon>
                      {{ newSessionSameSetupCopy.button }}
                  </v-btn>

                  <ConfirmDialog
                      v-model="new_session_same_setup_confirm_dialog"
                      :fullscreen="$vuetify.breakpoint.smAndDown"
                      cancel-text="Cancel"
                      confirm-text="Create session"
                      confirm-color="green darken-1"
                      @cancel="new_session_same_setup_confirm_dialog = false"
                      @confirm="confirmNewSessionSameSetup">
                      <h3 class="mb-3">{{ newSessionSameSetupCopy.title }}</h3>
                      <p class="mb-2">
                          Starting a new session will leave this session page.
                      </p>
                      <p class="mb-0">
                          {{ newSessionSameSetupCopy.body }}
                      </p>
                  </ConfirmDialog>
  
                  <v-btn small class="mt-4 w-100 session-action-btn" v-show="show_controls" :disabled="busy || state !== 'ready'" @click="openNewSessionConfirm">
                      <v-icon left small>mdi-plus</v-icon>
                      New session
                  </v-btn>

                  <ConfirmDialog
                      v-model="new_session_confirm_dialog"
                      :fullscreen="$vuetify.breakpoint.smAndDown"
                      icon="mdi-alert-circle"
                      icon-color="orange"
                      cancel-text="Cancel"
                      confirm-text="Continue"
                      confirm-color="orange darken-1"
                      @cancel="new_session_confirm_dialog = false"
                      @confirm="confirmNewSession">
                      <h3 v-if="newSessionCopy.title" class="mb-3">{{ newSessionCopy.title }}</h3>
                      <p class="mb-2">
                          {{ newSessionCopy.body }}
                      </p>
                      <p class="mb-0">
                          {{ newSessionCopy.detail }}
                      </p>
                  </ConfirmDialog>
  
                  <v-dialog v-model="dialog" content-class="app-dialog" :width="$vuetify.breakpoint.smAndDown ? '100%' : '500'"
                      max-width="500" :fullscreen="$vuetify.breakpoint.smAndDown">
                      <template v-slot:activator="{ on, attrs }">
                          <v-btn ref="shareDialogActivator" small class="mt-4 w-100 session-action-btn" v-bind="attrs" v-on="on" v-show="show_controls">
                              <v-icon left small>mdi-share-variant</v-icon>
                              Share session publicly
                          </v-btn>
                      </template>
  
                      <v-card>
                          <v-card-title class="text-h5">
                              Share session publicly
                          </v-card-title>
  
                          <v-card-text>
                              <v-checkbox id="session-public" v-model="session.public" name="session-public"
                                  label="Make session public" @change="setPublic($event)" />
  
                              <p>Making your session public will make your analyses public too.</p>
  
  
                              <v-container v-show="session.public">
                                  <h3 class="mb-2">Share on</h3>
                                  <ShareNetwork network="facebook" class="mr-2" style="text-decoration: none;"
                                      :url="sessionUrl" title="OpenCap session">
                                      <v-btn><v-icon aria-hidden="false">mdi-facebook</v-icon> &nbsp;Facebook</v-btn>
                                  </ShareNetwork>
                                  <ShareNetwork network="twitter" class="mr-2" style="text-decoration: none;"
                                      :url="sessionUrl" title="OpenCap session">
                                      <v-btn><v-icon aria-hidden="false">mdi-twitter</v-icon> &nbsp;Twitter</v-btn>
                                  </ShareNetwork>
                                  <ShareNetwork network="linkedin" :url="sessionUrl" style="text-decoration: none;"
                                      title="OpenCap session">
                                      <v-btn><v-icon aria-hidden="false">mdi-linkedin</v-icon> &nbsp;LinkedIn</v-btn>
                                  </ShareNetwork>
  
                                  <v-text-field label="Alternatively, copy this link"
                                      v-model="sessionUrl" class="mt-5" readonly
                                      autocomplete="off">
                                      <template v-slot:append-outer>
                                          <v-btn icon small @click="copySessionUrlToClipboard" :disabled="!sessionUrl"
                                              title="Copy to clipboard">
                                              <v-icon small>mdi-content-copy</v-icon>
                                          </v-btn>
                                      </template>
                                  </v-text-field>
                              </v-container>
  
                          </v-card-text>
  
                          <v-divider></v-divider>
  
                          <v-card-actions>
                              <v-spacer></v-spacer>
                              <v-btn color="primary" text @click="dialog = false">
                                  Close
                              </v-btn>
                          </v-card-actions>
                      </v-card>
                  </v-dialog>
  
  
                  <!-- Archive session -->
                  <v-btn small class="mt-4 w-100 session-action-btn" @click="showArchiveDialog = true">
                      <v-icon left small>mdi-download-outline</v-icon>
                      Download data
                  </v-btn>
                  <v-dialog
                      v-model="showArchiveDialog"
                      content-class="app-dialog"
                      max-width="500"
                      :fullscreen="$vuetify.breakpoint.smAndDown">
                      <v-card>
                          <v-card-text class="pt-4">
                              <v-row class="m-0">
                              <v-col cols="12" sm="2">
                                  <v-icon x-large color="green">mdi-download</v-icon>
                              </v-col>
                              <v-col cols="12" sm="10">
                                  <p v-if="isArchiveInProgress & !isArchiveDone">
                                      <v-progress-circular  indeterminate class="mr-2" color="grey" size="14" width="2" />
                                      Download in progress
                                  </p>
                                  <p v-if="!(isArchiveInProgress || isArchiveDone)">
                                      Do you want to download all the data from session <code>{{session.id}}</code>?
                                      (This can take several minutes).
                                  </p>
                                  <p v-if="isArchiveDone">
                                      The archive has been successfully generated. Click on data.zip to download.
                                  </p>
                              </v-col>
                              </v-row>
                          </v-card-text>
                          <v-card-actions>
                              <v-spacer></v-spacer>
                              <v-btn
                                  color="blue darken-1"
                                  text
                                  @click="showArchiveDialog = false;"
                              >
                                  Cancel
                              </v-btn>
                              <v-btn
                                  v-if="isArchiveDone"
                                  :href="archiveUrl"
                                  download="data.zip"
                              >
                                  data.zip
                              </v-btn>
                              <v-btn
                                  v-else
                                  color="green darken-1"
                                  text
                                  :disabled="isArchiveInProgress"
                                  @click="downloadSessionArchive(session.id)"
                              >
                                  Download
                              </v-btn>
                          </v-card-actions>
                      </v-card>
                  </v-dialog>
                  <!-- End archive session -->
  
                  <v-btn small v-if="isSyncDownloadAllowed" class="mt-4 w-100" :disabled="downloading" @click="onDownloadData">
                      <v-icon v-if="!downloading" left small>mdi-download</v-icon>
                      <v-progress-circular v-if="downloading" indeterminate class="mr-2" color="grey" size="14" width="2" />
                      Download data (old)
                  </v-btn>
  
                  <v-btn small class="mt-4 w-100 session-action-btn" :disabled="!hasKinematicsAvailable" @click="$router.push({ name: 'Dashboard', params: { id: session.id, trialId: trial.name  } })">
                      <v-icon left small>mdi-view-dashboard-outline</v-icon>
                      Dashboard kinematics
                  </v-btn>
  
                  <v-btn small class="mt-4 w-100 session-action-btn" v-show="show_controls" @click="$router.push({ name: 'SelectSession'})"
                      :disabled="busy || state !== 'ready'">
                      <v-icon left small>mdi-arrow-left</v-icon>
                      Back to session list
                  </v-btn>
            </div>
          </div>

            <!-- Mobile sidebar toggle - outside, right next to sidebar -->
            <v-btn
                v-if="leftMenuOpen && isTabletOrPhone"
                class="sidebar-toggle-btn ui-no-zoom"
                icon
                small
                @click="leftMenuOpen = false">
                <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
        </div><!-- end left-wrapper -->

        <div class="main-content d-flex flex-grow-1">
        <!-- Centered Open in App prompt for monocular mobile sessions -->
        <div v-if="showOpenInAppButton && !trial" class="open-in-app-center d-flex flex-column align-center justify-center">
            <v-icon size="80" color="primary-dark" class="mb-4">mdi-cellphone-arrow-down</v-icon>
            <h2 class="white--text mb-4 text-center">Ready to Record</h2>
            <p class="white--text text-center mb-6 px-4">Click the button below to start recording from the mobile app. Make sure you have the latest version of the OpenCap app (2.0+) installed. <a class="open-in-app-store-link" href="https://apps.apple.com/us/app/opencap/id1630513242" target="_blank" rel="noopener noreferrer">Get it on the App Store</a>.</p>
            <v-btn
                color="primary-dark"
                x-large
                @click="openInApp"
                class="open-in-app-btn">
                <v-icon left>mdi-launch</v-icon>
                Open in App
            </v-btn>
            <v-alert
                type="info"
                dense
                outlined
                class="monocular-beta-alert mt-6 mx-4"
                border="left">
              <div class="monocular-beta-content">
                <strong>Monocular mode is in beta.</strong>
                Jumping is not supported yet. Camera must be static at 30-45° angle. Keep the full body in frame, clearly visible (not blocked by objects or people), within 5 meters of the camera.
                <div class="d-flex flex-wrap mt-2">
                  <v-btn x-small outlined class="mr-2 mb-1" @click="window.open('https://www.opencap.ai/best-practices?variant=Monocular', '_blank')">Best practices</v-btn>
                  <v-btn x-small outlined class="mb-1" @click="window.open('https://www.opencap.ai/get-started?variant=monocular', '_blank')">Get started</v-btn>
                </div>
              </div>
            </v-alert>
        </div>

        <div class="viewer flex-grow-1" v-show="!showOpenInAppButton || trial">
            <div v-if="trial" class="d-flex flex-column" style="flex: 1 1 0; min-height: 0; height: 100%;">
  
                <div v-if="has3DData" class="mocap-wrapper flex-grow-1">
                  <div id="mocap" ref="mocap" class="mocap-inner" />
                  <div v-if="!sceneReady" class="mocap-loading-overlay d-flex align-center justify-center">
                    <v-progress-circular indeterminate color="grey lighten-1" size="40" width="3" />
                  </div>
                </div>
                <div v-else class="session-empty-state d-flex flex-column align-center justify-center text-center flex-grow-1">
                  <v-icon size="56" color="grey lighten-1" class="mb-3">mdi-cube-off-outline</v-icon>
                  <h3 class="mb-2">No 3D motion data for this trial</h3>
                  <p class="mb-0 px-4">This trial doesn’t have motion data to visualize. If videos exist, you can still preview them on the right.</p>
                </div>
  
  
                  <div v-if="trial && !isMobileOrTablet" class="video-controls ui-no-zoom d-flex flex-wrap align-center pa-2">
                      <v-text-field label="Time (s)" type="number" :step="0.01" :value="time"
                          :disabled="videoControlsDisabled || state !== 'ready'" class="time-input" @input="onChangeTime"
                          autocomplete="off" />
                      <v-slider :value="frame" :min="0" :max="frames.length - 1" :disabled="videoControlsDisabled" @input="onNavigate" hide-details
                          class="mb-2 flex-grow-1 timeline-slider" />

                      <div class="playback-controls-inline d-flex align-center">
                        <CameraViewControls
                            v-if="has3DData"
                            :value="cameraView"
                            :follow="followSubject"
                            :disabled="!sceneReady"
                            class="mr-2"
                            @change-view="setCameraView"
                            @update:follow="setFollowSubject" />

                        <VideoNavigation
                            :playing="playing"
                            :value="frame"
                            :maxFrame="frames.length - 1"
                            :loop="loopPlayback"
                            :show-loop-toggle="true"
                            :disabled="videoControlsDisabled"
                            @play="togglePlay(true)"
                            @pause="togglePlay(false)"
                            @toggle-loop="toggleLoopPlayback"
                            @input="onNavigate"
                            class="playback-navigation" />

                        <SpeedControl v-model="playSpeed" :disabled="videoControlsDisabled" class="playback-speed ml-2" />
                      </div>
                  </div>
              </div>
  
            <div v-else-if="trialLoading" class="flex-grow-1 d-flex align-center justify-center">
                <v-progress-circular indeterminate color="grey" size="30" width="4" />
            </div>
            <div v-else class="session-empty-state d-flex flex-column align-center justify-center text-center">
                <v-icon size="56" color="grey lighten-1" class="mb-3">mdi-chart-box-outline</v-icon>
                <h3 class="mb-2">No data selected/recorded</h3>
                <p v-if="show_controls" class="mb-0 px-4">Type a trial name on the left and click Start recording or select a trial from the list.</p>
                <p v-else class="mb-0 px-4">Select a trial from the list to view motion data.</p>
            </div>
        </div>
  
        <div class="right d-flex flex-column" :style="mobileVideoPanelStyle">
            <div class="videos d-flex flex-column">
              <video 
                  v-for="(video, index) in videos" 
                  :key="videoKey(video, index)" 
                  :ref="`video-${index}`" 
                  muted
                  playsinline 
                  :src="video.media" 
                  crossorigin="anonymous" 
                  @loadedmetadata="onVideoLoadedMetadata(index)"
                  @ended="onVideoEnded(index)"
                  preload="metadata"
                  class="video-element" />
            </div>

            <v-btn-toggle
                v-if="showCamSizeButton && !videoControlsDisabled"
                :value="mobileVideoSizeIndex"
                mandatory
                dense
                class="cam-size-overlay ui-no-zoom"
                @change="mobileVideoSizeIndex = $event">
              <v-btn x-small class="cam-size-segment">S</v-btn>
              <v-btn x-small class="cam-size-segment">M</v-btn>
              <v-btn x-small class="cam-size-segment">L</v-btn>
            </v-btn-toggle>

            <div v-if="isMobileOrTablet" class="right-spacer" />

            <div v-if="isMobileOrTablet && trial" class="playback-controls ui-no-zoom">
              <div class="playback-timeline-mobile d-flex align-center px-1">
                <v-text-field
                    label="Time (s)"
                    type="number"
                    :step="0.01"
                    :value="time"
                    :disabled="videoControlsDisabled || state !== 'ready'"
                    dense
                    hide-details
                    class="time-input mr-2"
                    autocomplete="off"
                    @input="onChangeTime" />
                <v-slider
                    :value="frame"
                    :min="0"
                    :max="frames.length - 1"
                    :disabled="videoControlsDisabled"
                    dense
                    hide-details
                    @input="onNavigate"
                    class="flex-grow-1 timeline-slider" />
              </div>

              <div class="playback-controls-row">
                <CameraViewControls
                    v-if="has3DData"
                    :value="cameraView"
                    :follow="followSubject"
                    :disabled="!sceneReady"
                    @change-view="setCameraView"
                    @update:follow="setFollowSubject" />

                <VideoNavigation
                    :playing="playing"
                    :value="frame"
                    :maxFrame="frames.length - 1"
                    :loop="loopPlayback"
                    :show-loop-toggle="true"
                    :show-skip-buttons="false"
                    :disabled="videoControlsDisabled"
                    @play="togglePlay(true)"
                    @pause="togglePlay(false)"
                    @toggle-loop="toggleLoopPlayback"
                    @input="onNavigate"
                    class="playback-navigation" />

                <SpeedControl v-model="playSpeed" :disabled="videoControlsDisabled" class="playback-speed" />
              </div>
            </div>
          </div>
        </div>
  
        <v-dialog
              v-model="trial_rename_dialog"
              content-class="compact-rename-dialog app-dialog"
              max-width="420"
              :fullscreen="$vuetify.breakpoint.smAndDown">
          <v-card>
            <v-btn
              icon
              small
              class="dialog-close-btn"
              aria-label="Close"
              title="Close"
              @click="trial_rename_dialog = false"
            >
              <v-icon small>mdi-close</v-icon>
            </v-btn>
            <v-card-text class="pt-4">
              <v-row class="m-0">
                <v-col cols="12" sm="2">
                  <v-icon x-large color="orange">mdi-rename-box</v-icon>
                </v-col>
                <v-col cols="12" sm="10">
                  <p v-if="session.trials[trial_rename_index]?.status === 'processing' || session.trials[trial_rename_index]?.status === 'uploading'" class="text-orange">
                      You can't rename a trial while it's being uploaded or processed. Please wait before attempting to rename the trial.
                  </p>
                  <p v-else>
                    Insert a new name for trial {{session.trials[trial_rename_index]?.name}}:
                  </p>
                  <ValidationObserver tag="div" class="d-flex flex-column" ref="observer_tr" v-slot="{ invalid }">
                    <ValidationProvider :rules="{required:true, alpha_dash_custom:true, unique_trial_name:[session.trials, session.trials[trial_rename_index]?.name]}" v-slot="{ errors }" name="Trial name">
  
                        <v-text-field v-model="trialNewName" label="Trial new name" class="flex-grow-0"
                            :disabled="state !== 'ready' || session.trials[trial_rename_index]?.status === 'processing' || session.trials[trial_rename_index]?.status === 'uploading'"
                                      :error="errors.length > 0" :error-messages="errors[0]"
                                      autocomplete="off"
                                      @keydown.enter.prevent="submitRenameTrial"
                                      @keydown.esc.prevent="trial_rename_dialog = false" />
                    </ValidationProvider>
  
                    <v-spacer></v-spacer>
  
                    <v-btn class="text-right" :disabled="invalid || session.trials[trial_rename_index]?.status === 'processing' || session.trials[trial_rename_index]?.status === 'uploading'"
                           @click="trial_rename_dialog = false; renameTrial(session.trials[trial_rename_index], trial_rename_index, trialNewName);">
                        Rename Trial
                    </v-btn>
                  </ValidationObserver>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Session rename dialog -->
        <v-dialog
          v-model="session_rename_dialog"
          content-class="compact-rename-dialog app-dialog"
          max-width="420"
          :fullscreen="$vuetify.breakpoint.smAndDown">
          <v-card>
            <v-btn
              icon
              small
              class="dialog-close-btn"
              aria-label="Close"
              title="Close"
              @click="session_rename_dialog = false"
            >
              <v-icon small>mdi-close</v-icon>
            </v-btn>
            <v-card-text class="pt-4">
              <v-row class="m-0">
                <v-col cols="12" sm="2">
                  <v-icon x-large color="orange">mdi-rename-box</v-icon>
                </v-col>
                <v-col cols="12" sm="10">
                  <p class="mb-1">Enter a new name for this session:</p>
                  <ValidationObserver tag="div" class="d-flex flex-column" ref="observer_session_rename" v-slot="{ invalid }">
            <ValidationProvider rules="required|alpha_dash_custom" v-slot="{ errors }" name="Session name">
              <v-text-field
                v-model="sessionNewName"
                label="Session name"
                class="flex-grow-0"
                :error="errors.length > 0"
                :error-messages="errors[0]"
                autocomplete="off"
                @keydown.enter.prevent="submitRenameSession"
                @keydown.esc.prevent="session_rename_dialog = false" />
                    </ValidationProvider>
                    <v-btn class="text-right" :disabled="invalid" @click="submitRenameSession">
                      Rename Session
                    </v-btn>
                  </ValidationObserver>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-dialog>

              <v-dialog
            v-model="trial_modify_tags"
            content-class="app-dialog"
            max-width="500"
            :fullscreen="$vuetify.breakpoint.smAndDown">
        <v-card>
          <v-btn
            icon
            small
            class="dialog-close-btn"
            aria-label="Close"
            title="Close"
            @click="trial_modify_tags = false"
          >
            <v-icon small>mdi-close</v-icon>
          </v-btn>
          <v-card-text class="pt-4">
            <v-row class="m-0">
              <v-col cols="12" sm="2">
                <v-icon x-large color="orange">mdi-rename-box</v-icon>
              </v-col>
              <v-col cols="12" sm="10">
                <p v-if="session.trials[trial_modify_tags_index]?.status === 'processing' || session.trials[trial_modify_tags_index]?.status === 'uploading'" class="text-orange">
                    You can't modify trial tags while it's being uploaded or processed. Please wait before attempting to modify the tags.
                </p>
                <p v-else>
                  Insert the tags for trial {{session.trials[trial_modify_tags_index]?.name}}:
                </p>
                <ValidationObserver tag="div" class="d-flex flex-column" ref="observer_tr_tag">
                  <ValidationProvider v-slot="{ errors }" name="Trial tags">
                  <v-autocomplete
                      ref="trialTagsSelect"
                      clearable
                      multiple
                      v-model="trialNewTags"
                      item-title="text"
                      item-value="value"
                      label="Trial Tags"
                      :items="tagsOptions"
                      :error="errors.length > 0"
                      :error-messages="errors[0]"
                      :search-input.sync="tag_search_input"
                      autocomplete="off"
                      @change="tag_search_input = ''"
                  ></v-autocomplete>
                  </ValidationProvider>
                  <v-spacer></v-spacer>
                  <v-btn class="text-right"
                         :disabled="session.trials[trial_modify_tags_index]?.status === 'processing' || session.trials[trial_modify_tags_index]?.status === 'uploading'"
                         @click="trial_modify_tags = false; modifyTagsTrial(session.trials[trial_modify_tags_index], trial_modify_tags_index, trialNewTags);">
                      Apply Tags
                  </v-btn>
                </ValidationObserver>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Trash trial dialog (extracted for mobile-friendly behavior) -->
      <ConfirmDialog
        v-if="trialForTrashDialog"
        v-model="remove_dialog"
        :click-outside="clickOutsideDialogTrialHideMenu"
        :fullscreen="$vuetify.breakpoint.smAndDown"
        icon="mdi-close-circle"
        icon-color="red"
        confirm-color="red darken-1"
        @cancel="closeTrashDialog"
        @confirm="confirmTrashTrial">
        <p>
          Do you want to trash trial {{ trialForTrashDialog.name }}?
          You will be able to restore it for 30 days. After that,
          this trial will be permanently removed.
        </p>
      </ConfirmDialog>

      <!-- Restore trial dialog -->
      <ConfirmDialog
        v-if="trialForRestoreDialog"
        v-model="restore_dialog"
        :click-outside="clickOutsideDialogTrialHideMenu"
        :fullscreen="$vuetify.breakpoint.smAndDown"
        icon="mdi-undo-variant"
        icon-color="green"
        @cancel="closeRestoreDialog"
        @confirm="confirmRestoreTrial">
        <p>
          Do you want to restore trial {{ trialForRestoreDialog.name }}?
        </p>
      </ConfirmDialog>

      <!-- Permanent delete trial dialog -->
      <ConfirmDialog
        v-if="trialForPermanentDeleteDialog"
        v-model="permanent_delete_dialog"
        :click-outside="clickOutsideDialogTrialHideMenu"
        :fullscreen="$vuetify.breakpoint.smAndDown"
        icon="mdi-close-circle"
        icon-color="red"
        confirm-color="red darken-1"
        @cancel="closePermanentDeleteDialog"
        @confirm="confirmPermanentDeleteTrial">
        <p>
          Do you want to permanently delete trial {{ trialForPermanentDeleteDialog.name }}?
          This action cannot be undone.
          <span v-if="!trialForPermanentDeleteDialog.trashed"> Use Trash to keep the ability to restore the trial.</span>
        </p>
      </ConfirmDialog>
  
    <v-dialog
        v-model="showAnalysisDialog"
        v-click-outside="clickOutsideDialogTrialHideMenu"
        content-class="app-dialog"
        max-width="fit-content"
        :fullscreen="$vuetify.breakpoint.smAndDown">
      <v-card>
          <v-card-title class="text-h5 font-weight-bold pb-2">
            <v-icon left>mdi-chart-box-outline</v-icon>
            Advanced Analysis
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text v-if="analysisFunctions.length > 0" class="pt-4">
                  <template v-if="session?.trials && session.trials[trial_analysis_index]">
                  <div v-for="(func, index) in analysisFunctions"
                      v-bind:item="func"
                      v-bind:index="index"
                      v-bind:key="func.id"
                      :ref="func.id">
                    
                  <v-row class="align-center mb-2">
                  <v-col cols="12" sm="4" class="py-2">
                    <div class="font-weight-bold text-subtitle-1 d-flex align-center">
                      <span>{{ func.title }}</span>
                      <v-tooltip bottom v-if="func.info.length > 0" max-width="320px">
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" small color="grey" class="ml-1"> mdi-help-circle-outline </v-icon>
                        </template>
                        <p v-html="func.info.replace(/\n/g, '<br>')" />
                      </v-tooltip>
                    </div>
                  </v-col>
                  <v-col cols="12" sm="5" class="py-2">
                    <div class="text-body-2 grey--text text--darken-2">{{ func.description }}</div>
                  </v-col>
                  <v-col cols="12" sm="3" class="py-2 text-right">
                    <v-btn small :color="analysisActionBtnColor" :dark="$vuetify.theme.dark" elevation="2" v-if="func.trials.includes(session.trials[trial_analysis_index].id)" :disabled="session.trials[trial_analysis_index].id in func.trials">
                        <span >
                            <v-progress-circular  indeterminate class="mr-2" color="grey" size="14" width="2" />
                            Calculating...
                        </span>
                    </v-btn>

                    <v-btn
                        small
                        elevation="2"
                        :color="analysisActionBtnColor"
                        :dark="$vuetify.theme.dark"
                        v-if="!func.trials.includes(session.trials[trial_analysis_index].id) && !(session.trials[trial_analysis_index].id in func.states)"
                        @click="invokeAnalysisFunction(func.id, session.trials[trial_analysis_index].id, session.trials[trial_analysis_index]?.name)"
                        >
                        <v-icon left small>mdi-play</v-icon>
                        Run
                    </v-btn>
                      <v-btn
                        small
                        elevation="2"
                        :color="analysisActionBtnColor"
                        :dark="$vuetify.theme.dark"
                        v-if="(session.trials[trial_analysis_index].id in func.states) && !func.trials.includes(session.trials[trial_analysis_index].id)"
                        @click="func.states[session.trials[trial_analysis_index].id].state === 'successfull' && func.states[session.trials[trial_analysis_index].id].dashboard_id != null && goToAnalysisDashboard(func.states[session.trials[trial_analysis_index].id].dashboard_id, session.trials[trial_analysis_index].id)"
                      >
                          <span :style="func.states[session.trials[trial_analysis_index].id].state == 'failed'? 'color:red' : 'color:lightgreen'" class="font-weight-bold">{{ func.states[session.trials[trial_analysis_index].id].state }}</span>
                          <v-menu offset-y left close-on-content-click content-class="analysis-submenu">
                              <template v-slot:activator="{ on, attrs }">
                              <v-btn icon :dark="$vuetify.theme.dark" v-bind="attrs" v-on="on" class="analysis-menu-btn" @click.stop>
                                  <v-icon>mdi-menu</v-icon>
                              </v-btn>
                              </template>

                              <v-list class="analysis-submenu-list">
                                  <v-list-item link
                                      @click="invokeAnalysisFunction(func.id, session.trials[trial_analysis_index].id, session.trials[trial_analysis_index]?.name)"
                                      :disabled="trial_analysis_index in func.trials">
                                      <v-icon left small>mdi-refresh</v-icon>
                                      Re-run
                                  </v-list-item>
                                  <v-list-item
                                      @click="goToAnalysisDashboard(func.states[session.trials[trial_analysis_index].id].dashboard_id, session.trials[trial_analysis_index].id)"
                                      v-if="func.states[session.trials[trial_analysis_index].id].dashboard_id != null && func.states[session.trials[trial_analysis_index].id].state == 'successfull'"
                                      >
                                      <v-icon left small>mdi-view-dashboard</v-icon>
                                      Analysis Dashboard
                                    </v-list-item>
                                   <v-list-item
                                     v-for="menu_item in func.states[session.trials[trial_analysis_index].id].menu"
                                     @click="requestDownloadMenuItem(session.trials[trial_analysis_index], menu_item)" :key="menu_item.label"
                                      >
                                      <v-icon left small>mdi-download</v-icon>
                                      {{ menu_item.label }}
                                    </v-list-item>
                              </v-list>

                          </v-menu>
                      </v-btn>

                  </v-col>
              </v-row>
              
              <v-divider v-if="index < analysisFunctions.length - 1" class="my-3"></v-divider>
              </div>
                  </template>
                  <p v-else class="mb-0 grey--text">Select a trial to run analysis.</p>
          </v-card-text>
          <v-card-text v-else class="text-center py-8">
              <v-icon size="64" color="grey lighten-1">mdi-function-variant</v-icon>
              <p class="text-h6 grey--text mt-4">No available functions</p>
              <p class="grey--text text--lighten-1">There are no analysis functions available for this session.</p>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn
          color="blue darken-1"
          text
          v-if="analysisFunctions.length > 0"
        >
          <v-icon left small>mdi-restore</v-icon>
          Reset results
        </v-btn>
        <v-btn
          color="red darken-1"
          text
          @click="showTrialMenuSheet = false; selectedTrialForMenu = null; (session.trials[trial_analysis_index] || {}).isMenuOpen = false; showAnalysisDialog = false;"
        >
          <v-icon left small>mdi-close</v-icon>
          Close
        </v-btn>
          </v-card-actions>
      </v-card>
      </v-dialog>
      </div>
  </template>
  
  <script>
  import moment from 'moment'
  import Vue from 'vue'
  import momentDurationFormatSetup from 'moment-duration-format'
  import axios from 'axios'
  import { mapState, mapMutations, mapActions } from 'vuex'
  import { apiError, apiErrorRes, apiSuccess, clearToastMessages } from '@/util/ErrorMessage.js'
  import { playRecordingSound, playRecordingFinishedSound } from "@/util/SoundMessage.js";
  import { getSessionDeepLink } from '@/util/SessionDeepLink.js'
  import { axiosGetWithRetry } from "@/util/network.js";
  import Status from '@/components/ui/Status'
  import * as THREE from 'three'
  import * as THREE_OC from '@/orbitControls'
  import VideoNavigation from '@/components/ui/VideoNavigation'
  import CameraViewControls from '@/components/ui/CameraViewControls'
  import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js'
  import SpeedControl from '@/components/ui/SpeedControl'
  import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
  import { getPelvisPose, placeCameraForView, followSubjectHorizontally, snapFollowToSubject } from '@/util/cameraViews'
  import { debounce } from 'lodash'
  import { isTester, loadUserGroups } from '@/util/staffAccess.js'

  let openpose_bones = [
    [20, 21],
    [20, 22],
    [21, 43],
    [22, 44],
    [43, 47],
    [44, 48],
    [20, 25],
    [20, 26],
  
    //    [25, 23],
    //    [26, 24],
  
    [25, 29],
    [26, 30],
    [29, 33],
    [30, 34],
    [33, 37],
    [34, 38],
  ]
  
  var a0 = 0;
  var a1 = 1;
  var a2 = 2;
  
  const objLoader = new OBJLoader();
  
  export default {
      name: 'Session',
      components: {
          Status,
          VideoNavigation,
          CameraViewControls,
          SpeedControl,
          ConfirmDialog
      },
      data() {
          return {
              startButtonCaptions: {
                  ready: 'Start recording',
                  recording: 'Stop recording',
                  processing: 'Cancel Upload'
              },
  
              rename_dialog: false,
              remove_dialog: false,
              restore_dialog: false,
              permanent_delete_dialog: false,
              show_trashed: false,
              menu: [],
              busy: false,
              state: 'ready',
              submitted: false,
              trialName: '',
              trialNewName: '',
              trialTags: [],
              trialNewTags: [],
              statusPoll: null,
              downloading: false,
              dialog: null,

              tag_search_input: '',
  
              showArchiveDialog: false,
              isArchiveInProgress: false,
              isArchiveDone: false,
              archiveUrl: "#",
  
              showAnalysisDialog: false,
              showAnalysisResultDialog: false,
              trial_analysis_index: 0,
              // isInvokeInProgress: false,
              // isInvokeDone: false,
              // analysisResult: {analysis_function: {}, result: { meta: {}}},
              // invokedFunctionId: null,
              // invokedAnalysisFunctionTrialName: null,
              // shownAnalysisDialogTrialName: null,
  
              trialInProcess: null,
              trial: null,
              videos: [],
              frames: [],
              trialLoading: false,
              sceneReady: false,
  
              // objects & arrays
              synced: false,
              camera: null,
              scene: null,
              renderer: null,
              controls: null,
              pose_spheres: [],
              pose_bones: [],
              meshes: {},
              frame: 0,
              time: 0,
              playing: false,
              loopPlayback: true,
              playSpeed: 1,
              mobileVideoSizeIndex: 0,
              cameraView: 'default',
              followSubject: false,
              followSmoothedLookAt: null,
  
              show_controls: 1,
  
              resizeObserver: null,
  
              recordingStarted: null,
              recordingTimePassed: 0,
              recordingTimer: null,
              recordingStatusPoll: null,
              recordingActionInFlight: false,

              // Reactive clock + timer used to count down the LiDAR switch cooldown
              lidarCooldownNow: Date.now(),
              lidarCooldownTimer: null,

              trialsPoll: null,
              trialsPollActive: false,
              showSessionMenuButtons: false,
              leftMenuOpen: false,
              trialLegendOpen: false,
  
              n_calibrated_cameras: 0,
              n_cameras_connected: 0,
              n_cameras_using_lidar: 0,
              n_videos_uploaded: 0,
              recordingFramerate: null,
  
              trial_rename_dialog: false,
              trial_rename_index: 0,

              session_rename_dialog: false,
              new_session_confirm_dialog: false,
              new_session_same_setup_confirm_dialog: false,
              sessionNewName: '',

              trial_modify_tags: false,
              trial_modify_tags_index: 0,

              // Mobile trial menu - trial whose options are shown in bottom sheet
              showTrialMenuSheet: false,
              selectedTrialForMenu: null,
              // Trial context for Trash/Restore/Delete dialogs (extracted from v-for for proper mobile UX)
              trialForTrashDialog: null,
              trialForRestoreDialog: null,
              trialForPermanentDeleteDialog: null,

              isAuditoryFeedbackEnabled: false,
              controlGestureGuard: null,

              sessionNotification: { show: false, text: '', type: 'error' },
              userGroups: [],

              // Performance optimization properties
              lastFrameTime: 0,
              renderLoopActive: false,
              frameSkipCounter: 0,
              maxFrameSkip: 2,
              materialCache: new Map(),
              dataCache: new Map(),
              renderQuality: 'high', // 'low', 'medium', 'high'
              isLowPerformance: false,
          }
      },
      filters: {
          nl2br: function (value) {
              return value.replace(/\n/g, '<br>')
          }
      },
      computed: {
          ...mapState({
            session: state => state.data.session,
            sessions: state => state.data.sessions,
            analysisFunctions: state => state.data.analysisFunctions,

            trialTagsList: state => state.data.trialTags,

            user_id: state => state.auth.user_id,
  
            // step Calibration data
            rows: state => state.data.rows,
            cols: state => state.data.cols,
            squareSize: state => state.data.squareSize,
            placement: state => state.data.placement,
  
            // step Neutral data
            identifier: state => state.data.identifier,
            weight: state => state.data.weight,
            height: state => state.data.height,
            gender: state => state.data.gender,
            isSyncDownloadAllowed: state => state.data.isSyncDownloadAllowed,
            lidarSwitchCooldownUntil: state => state.data.lidarSwitchCooldownUntil
          }),
        sessionUrl() {
          return location.origin + "/session/" + (this.session?.id || '');
        },
        analysisActionBtnColor() {
          return this.$vuetify.theme.dark ? 'grey darken-4' : 'primary'
        },
        isDarkTheme() {
          return this.$vuetify.theme.dark
        },
        displaySessionName() {
          const s = this.session;
          if (!s) return 'Session';
          return s.meta?.sessionName || s.sessionName || (s.id ? String(s.id).split('-')[0] : '') || 'Session';
        },
        participantName() {
          const session = this.session
          if (!session) return ''

          const hasSubject = session.subject || session.meta?.subject?.id
          const name = session.subject_name || (hasSubject ? session.name : '') || ''
          return String(name)
        },
        sessionFramerate() {
          const framerate = this.session?.meta?.settings?.framerate ?? null;
          return framerate !== null ? Number(framerate) : null;
        },
        recordingDisplayFramerate() {
          return this.recordingFramerate ?? this.sessionFramerate
        },
        filteredTrialsWithMenu() {
          return this.filteredTrials.map(trial => ({...trial, isMenuOpen: false}));
        },
        filteredTrials() {
          const trials = this.session?.trials || []
          return trials.filter(trial => trial && trial.name !== 'calibration' && !(trial.name === 'neutral' && trial.status === 'error')).filter(t => this.show_trashed || !t.trashed)
        },
        selectedTrialFromList() {
          if (!this.trial?.id || !Array.isArray(this.session?.trials)) {
            return null
          }
          return this.session.trials.find(trial => trial.id === this.trial.id) || null
        },
        videoControlsDisabled() {
          return !this.trial || this.trial.name === 'neutral' || this.frames.length === 0
        },
        has3DData() {
          return !!this.trial && Array.isArray(this.frames) && this.frames.length > 0
        },
        hasKinematicsAvailable() {
          if (!this.trial || !this.trial.results) return false
          return this.trial.results.some(r => r.tag === 'visualizerTransforms-json')
        },
        lidarCooldownRemaining() {
          if (!this.lidarSwitchCooldownUntil) return 0
          const msLeft = this.lidarSwitchCooldownUntil - this.lidarCooldownNow
          return msLeft > 0 ? Math.ceil(msLeft / 1000) : 0
        },
        lidarCooldownActive() {
          // Only block starting a new recording, never stopping an ongoing one.
          return this.state === 'ready' && this.lidarCooldownRemaining > 0
        },
        recordingStopCooldownActive() {
          return this.state === 'recording' && this.recordingTimePassed < 2
        },
        buttonCaption() {
          if (this.lidarCooldownActive) {
            return `Switching camera… ${this.lidarCooldownRemaining}s`
          }
          switch (this.state) {
            case 'recording': {
              const time = moment
                  .duration(this.recordingTimePassed, 'seconds')
                  .format('*mm:ss')
              return `${this.startButtonCaptions[this.state]} ${time}`
            }
            default: {
              return this.startButtonCaptions[this.state]
            }
          }
        },
        tagsOptions () {
            return Object.entries(this.trialTagsList).map((s) => ({ text: s[1], value: s[0] }))
        },
        isMobileOrTablet() {
          return this.$vuetify.breakpoint.smAndDown
        },
        isPhone() {
          return this.$vuetify.breakpoint.xsOnly
        },
        isTabletOrPhone() {
          return this.$vuetify.breakpoint.mdAndDown
        },
        showCamSizeButton() {
          return this.$vuetify.breakpoint.mdAndDown
        },
        isMonocularSession() {
          return !!(this.session?.isMono ?? this.session?.is_mono)
        },
        newSessionSameSetupCopy() {
          if (this.isMonocularSession) {
            return {
              button: 'New session, same camera',
              title: 'New session, same camera',
              body: 'The new session will use the currently connected recording camera. If you are recording on this same device, it must be a supported iPhone or iPad with OpenCap app 2.0 or newer.',
            }
          }
          return {
            button: 'New session, same setup',
            title: 'New session, same setup',
            body: 'The new session will keep the current calibration, recording setup, and saved advanced settings.',
          }
        },
        newSessionCopy() {
          if (this.isMonocularSession) {
            return {
              title: 'New monocular setup',
              body: 'Starting a new session will leave this session page.',
              detail: 'You may need to connect a recording device again by opening the session in the OpenCap app or scanning the QR code.',
            }
          }
          return {
            title: '',
            body: 'Starting a new session will require calibration again.',
            detail: 'To keep the current calibration and recording setup, choose New session, same setup instead.',
          }
        },
        sessionDeepLinkUrl() {
          if (!this.session?.id) return null
          const token = typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null
          const subjectName = this.session?.subject_name || null
          return getSessionDeepLink(this.session.id, token, subjectName)
        },
        isSameDevice() {
          if (this.$route.query.sameDevice === 'true') {
            return true
          }
          return this.isSessionMarkedSameDevice(this.session?.id)
        },
        showOpenInAppButton() {
          return this.$vuetify.breakpoint.mdAndDown && this.isMonocularSession && this.isSameDevice && this.session?.id && this.sessionDeepLinkUrl
        },
        hasRecordedTrial() {
          return this.filteredTrials.some(t => t.status && t.status !== 'ready')
        },
        // Use max of API values so monocular (n_calibrated=1) still shows actual device count when more are recording
        displayDeviceCount() {
          return Math.max(this.n_cameras_connected, this.n_videos_uploaded, 1)
        },
        isLidarRecordingEnabled() {
          return this.parseTruthy(this.session?.useLidar ?? this.session?.use_lidar)
        },
        isTesterUser() {
          return isTester({ groups: this.userGroups })
        },
        displayLidarDeviceCount() {
          return Math.max(this.n_cameras_using_lidar, 0)
        },
        recordingStatusText() {
          return `${this.displayDeviceCount} ${this.pluralize(this.displayDeviceCount, 'camera', 'cameras')} recording`
        },
        lidarRecordingStatusText() {
          return `${this.displayLidarDeviceCount} with lidar`
        },
        isSaveLocalPage() {
          return this.parseTruthy(this.session?.save_local ?? this.session?.saveLocal)
        },
        savedLocallyProgressText() {
          const videoText = this.n_videos_uploaded === 1 ? 'video' : 'videos'
          const savedLabel = `${this.n_videos_uploaded} ${videoText} saved locally`
          if (this.isMonocularSession) {
            return `${savedLabel}, do not refresh.`
          }

          const calibratedLabel = `${this.n_calibrated_cameras} camera${this.n_calibrated_cameras === 1 ? '' : 's'} calibrated`
          return `${calibratedLabel}, ${savedLabel}, do not refresh.`
        },
        uploadedProgressText() {
          const uploadedLabel = this.pluralize(this.n_videos_uploaded, 'video', 'videos')
          const expectedLabel = this.pluralize(this.displayDeviceCount, 'video', 'videos')
          return `${this.n_videos_uploaded} ${uploadedLabel} of ${this.displayDeviceCount} ${expectedLabel} uploaded, do not refresh.`
        },
        processingProgressText() {
          return this.isSaveLocalPage
            ? this.savedLocallyProgressText
            : this.uploadedProgressText
        },
        transferProgressDescription() {
          if (this.isSaveLocalPage) {
            const videoText = this.n_videos_uploaded === 1 ? 'video was' : 'videos were'
            return `${this.n_videos_uploaded} ${videoText} saved locally`
          }

          const videoText = this.n_videos_uploaded === 1 ? 'video was' : 'videos were'
          return `${this.n_videos_uploaded} ${videoText} uploaded`
        },
        mobileVideoSizeLabel() {
          return ['S', 'M', 'L'][this.mobileVideoSizeIndex] || 'S'
        },
        mobileVideoPanelStyle() {
          if (!this.showCamSizeButton) {
            return {}
          }

          const widths = [120, 150, 180]
          const minWidths = [100, 120, 140]
          const maxWidths = ['35%', '45%', '55%']
          const maxHeight = [120, 150, 180]
          const idx = this.mobileVideoSizeIndex

          return {
            width: `${widths[idx]}px`,
            minWidth: `${minWidths[idx]}px`,
            maxWidth: maxWidths[idx],
            '--mobile-video-max-height': `${maxHeight[idx]}px`
          }
        },
        renderSettings() {
          if (this.isLowPerformance) {
            return {
              pixelRatio: 1.5,
              shadowMapSize: 1024,
              antialiasing: false,
              maxFramesToRender: 60
            };
          }
          return {
            pixelRatio: window.devicePixelRatio || 2,
            shadowMapSize: 2048,
            antialiasing: true,
            maxFramesToRender: 60
          };
        }
      },
    async mounted() {
      // Check performance
      this.checkPerformance();

      await this.loadSession(this.$route.params.id)
      this.persistSameDeviceSessionFlag()

      // Keep sidebar always open on tablet/desktop; preserve current drawer behavior on phones.
      if (!this.isPhone) {
        this.leftMenuOpen = true
      } else if (!this.showOpenInAppButton || this.hasRecordedTrial) {
        this.leftMenuOpen = true
      }

      this.loadTrialTags()
      this.loadUserGroupsForAccess()

      // Check if something went wrong with loading session. Usually there was a redirect to Login page.
      if (this.session.id == undefined) {
        return
      }
      // Get number of expected cameras. Skip for monocular sessions (no calibration).
      if (this.isMonocularSession) {
        this.n_calibrated_cameras = 1
      } else {
        try {
          const res = await axios.get(`/sessions/${this.session.id}/get_n_calibrated_cameras/`, {})
          this.n_calibrated_cameras = res.data.data
        } catch (e) {
          this.n_calibrated_cameras = 0
        }
      }

      await this.syncInitialSessionStateFromStatus()

      if (this.user_id == this.session.user) {
        this.show_controls = true
        this.showSessionMenuButtons = false

        await this.loadAnalysisFunctions()
        await this.loadAnalysisFunctionsPending()
        await this.loadAnalysisFunctionsStates()
        await this.analysisFunctionsPolls()
      } else {
        this.show_controls = false
        this.showSessionMenuButtons = false
      }
  
      console.log(this.user_id)
      console.log(this.session.user)
  
      this.startTrialsPoll()
  
      const doneTrials = this.filteredTrials.filter(trial => trial.status === 'done')
  
      if (this.state === 'ready' && doneTrials.length > 0) {
        console.log("Done trials:")
        console.log(doneTrials[0])
        this.loadTrial(doneTrials[0])
      }

      // Add keyboard event listener
      window.addEventListener('keydown', this.handleTrialListShortcut)
      window.addEventListener('keydown', this.handleKeyboard)
      window.addEventListener('resize', this.onResize)
      this.bindControlGestureGuards()
    },
    beforeDestroy() {
      this.cancelPoll()
      this.cancelRecordTimer()
      this.cancelRecordingStatusPoll()
      this.cancelTrialsPoll()
      this.stopLidarCooldownTicker()
  
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }

      // Remove keyboard event listener
      window.removeEventListener('keydown', this.handleTrialListShortcut)
      window.removeEventListener('keydown', this.handleKeyboard)
      window.removeEventListener('resize', this.onResize)
      this.unbindControlGestureGuards()
      this.setTrialLegendOutsideClickListener(false)

      // Clear caches
      this.materialCache.clear();
      this.dataCache.clear();
      this.disposeScene();
    },
    watch: {
      lidarSwitchCooldownUntil: {
        immediate: true,
        handler() {
          this.startLidarCooldownTicker()
        }
      },
      dialog(isOpen) {
        if (!isOpen) {
          this.$nextTick(() => {
            const el = this.$refs.shareDialogActivator?.$el
            if (el && typeof el.blur === 'function') el.blur()
          })
        }
      },
      trial() {
        if (!this.trial || !this.has3DData) {
          if (this.resizeObserver) {
            this.resizeObserver.disconnect()
            this.resizeObserver = null
          }
        }
      },
      leftMenuOpen() {
        this.$nextTick(() => this.onResize())
      },
      playSpeed() {
        this.eachVideo(videoElement => {
          videoElement.playbackRate = this.playSpeed
        })
      },
      isDarkTheme() {
        if (this.renderer) {
          this.renderer.setClearColor(this.sceneBackgroundColor())
          this.animateOneFrame()
        }
      },
      showArchiveDialog(newShowArchiveDialog, oldShowArchiveDialog) {
        if (!newShowArchiveDialog) {
          this.isArchiveDone = false;
          this.isArchiveInProgress = false;
          this.archiveUrl = "#";
        }
      },
      showOpenInAppButton: {
        handler(val) {
          if (val && this.isPhone && !this.hasRecordedTrial) {
            this.leftMenuOpen = false
          }
        }
      },
      hasRecordedTrial: {
        handler(val) {
          if (val && this.isPhone && !this.showOpenInAppButton) {
            this.leftMenuOpen = true
          }
        }
      },
      // showAnalysisDialog(newShowAnalysisDialog, oldShowAnalysisDialog){
      //     console.log(newShowAnalysisDialog);
      //     // if(!newShowAnalysisDialog){
      //     //     this.shownAnalysisDialogTrialName = null;
      //     //     if(!this.isInvokeInProgress){
      //     //         this.isInvokeDone = false;
      //     //         this.analysisResult = {analysis_function: {}, response: {}};
      //     //         this.invokedFunctionId = null;
      //     //     }
      //     // }
      // }
    },
    created() {
      // Load the initial value from localStorage
      const storedValue = localStorage.getItem("auditory_feedback");
      this.isAuditoryFeedbackEnabled = storedValue === "true";
    },
    methods: {
      ...mapMutations('data', [
        'setSession',
        'setSessionStep5',
        'clearAll',
        'setSessionId',
        'addTrial',
        'updateTrial',
        'setAnalysisFunctionTrial',
        'setAnalysisFunctionResult',
        'setAnalysisFunctionState',
        'removeAnalysisFunctionTrial',
        'resetAnalysisFunctionResult'
      ]),
      ...mapActions('data', [
        'loadSession',
        'initSessionSameSetup',
        'loadAnalysisFunctions', 'loadAnalysisFunctionsPending', 'loadAnalysisFunctionsStates', 'loadTrialTags']),
      async loadUserGroupsForAccess() {
        try {
          this.userGroups = await loadUserGroups()
        } catch {
          this.userGroups = []
        }
      },
      toggleTrialLegend() {
        this.trialLegendOpen = !this.trialLegendOpen
        this.setTrialLegendOutsideClickListener(this.trialLegendOpen)
      },
      closeTrialLegend() {
        if (!this.trialLegendOpen) return
        this.trialLegendOpen = false
        this.setTrialLegendOutsideClickListener(false)
      },
      onTrialLegendClickOutside(event) {
        const wrap = this.$el && this.$el.querySelector('.trial-legend-wrap')
        if (wrap && wrap.contains(event.target)) return
        this.closeTrialLegend()
      },
      setTrialLegendOutsideClickListener(active) {
        if (active) {
          document.addEventListener('click', this.onTrialLegendClickOutside, true)
          document.addEventListener('touchend', this.onTrialLegendClickOutside, true)
        } else {
          document.removeEventListener('click', this.onTrialLegendClickOutside, true)
          document.removeEventListener('touchend', this.onTrialLegendClickOutside, true)
        }
      },
      parseTruthy(raw) {
        if (raw === true || raw === '') return true
        if (raw === false || raw == null) return false
        return ['true', '1', 'yes', 'on'].includes(String(raw).toLowerCase())
      },
      pluralize(count, singular, plural = `${singular}s`) {
        return Number(count) === 1 ? singular : plural
      },
      applyStatusCounts(data = {}) {
        if (typeof data.n_cameras_connected !== 'undefined') {
          this.n_cameras_connected = data.n_cameras_connected
        }
        if (typeof data.n_videos_uploaded !== 'undefined') {
          this.n_videos_uploaded = data.n_videos_uploaded
        }
        if (typeof data.n_cameras_using_lidar !== 'undefined') {
          this.n_cameras_using_lidar = data.n_cameras_using_lidar
        }
        const framerate = this.getStatusFramerate(data)
        if (framerate !== null) {
          this.recordingFramerate = framerate
        }
      },
      getStatusFramerate(data = {}) {
        if (typeof data.framerate !== 'undefined' && data.framerate !== null && data.framerate !== '') {
          const parsed = Number(data.framerate)
          return Number.isNaN(parsed) ? data.framerate : parsed
        }

        return null
      },
      mergeReturnedTrials(updatedTrials = []) {
        updatedTrials.forEach(updatedT => {
          const existingIndex = this.session.trials.findIndex(t => t.id === updatedT.id)
          if (existingIndex < 0) {
            this.addTrial(updatedT)
          } else {
            this.updateTrial(updatedT)
          }
        })
      },
      async syncInitialSessionStateFromStatus() {
        try {
          const res = await axios.get(`/sessions/${this.session.id}/status/?ret_session=True`)
          this.applyStatusCounts(res.data)

          const returnedTrials = res.data.session?.trials || []
          this.mergeReturnedTrials(returnedTrials)

          if (res.data.status === 'uploading' || res.data.status === 'processing') {
            this.state = 'processing'
            this.trialInProcess = returnedTrials.find(trial => ['uploading', 'processing'].includes(trial.status)) || null
            this.startPoll()
            return
          }

          if (res.data.status === 'ready') {
            const recordingTrial = returnedTrials.find(trial => trial.status === 'recording')
            if (recordingTrial) {
              this.state = 'recording'
              this.trialInProcess = recordingTrial
              this.recordingStarted = moment()
              this.recordingTimePassed = 0
              this.cancelRecordTimer()
              this.recordingTimer = window.setTimeout(this.recordTimerHandler, 500)
              this.startRecordingStatusPoll()
            }
          }
        } catch (error) {
          // Keep the default ready state if the initial status refresh fails.
        }
      },
      bindControlGestureGuards() {
        this.controlGestureGuard = (event) => {
          const target = event.target
          if (!target || !target.closest || !target.closest('.ui-no-zoom')) {
            return
          }

          if (event.type === 'touchmove') {
            if (event.touches && event.touches.length > 1) {
              event.preventDefault()
            }
            return
          }

          event.preventDefault()
        }

        document.addEventListener('gesturestart', this.controlGestureGuard, { passive: false })
        document.addEventListener('gesturechange', this.controlGestureGuard, { passive: false })
        document.addEventListener('touchmove', this.controlGestureGuard, { passive: false })
      },
      unbindControlGestureGuards() {
        if (!this.controlGestureGuard) {
          return
        }

        document.removeEventListener('gesturestart', this.controlGestureGuard)
        document.removeEventListener('gesturechange', this.controlGestureGuard)
        document.removeEventListener('touchmove', this.controlGestureGuard)
        this.controlGestureGuard = null
      },
      videoKey(video, index) {
        const trialId = this.trial?.id ?? 'no-trial'
        const media = video?.media ?? video?.video ?? ''
        const id = video?.id ?? ''
        // Tie key to the active trial so DOM can't be reused across trials.
        return `trial-${trialId}-video-${id || media || index}`
      },
      recordingActionMinLockDuration(state) {
        return state === 'processing' ? 1500 : 1000
      },
      async waitForRecordingActionMinLock(startedAt, duration) {
        const remaining = Math.max(0, duration - (Date.now() - startedAt))
        if (remaining > 0) {
          await new Promise(resolve => window.setTimeout(resolve, remaining))
        }
      },
      async changeState() {
        if (this.recordingActionInFlight) return
        this.recordingActionInFlight = true
        const actionStartedAt = Date.now()
        const minLockDuration = this.recordingActionMinLockDuration(this.state)
        let enforceMinLock = false

        try {
          switch (this.state) {
            case 'ready': {
              // Block starting a recording while the phone is switching capture
              // pipelines (AVFoundation <-> ARKit) after a LiDAR toggle.
              if (this.lidarCooldownActive) return

              this.submitted = true
    
              if (await this.$refs.observer.validate()) {
                this.busy = true
                this.recordingTimePassed = 0
    
                try {
                  // store in vuex
                  this.setSessionStep5(this.trialName)
    
                  // api
                  const res = await axiosGetWithRetry(`/sessions/${this.session.id}/record/`, {
                    params: {
                      name: this.trialName
                    }
                  }, { retries: 2, backoffFactor: 0.2, maxJitterMs: 100, timeout: 4500 })
    
                  // add to the list
                  this.trialInProcess = res.data
                  this.addTrial(this.trialInProcess)

                  // Get n_cameras_connected.
                  const res_status = await axiosGetWithRetry(`/sessions/${this.session.id}/status/`, {}, { retries: 1, backoffFactor: 0.2, maxJitterMs: 100, timeout: 2000 })
                  this.applyStatusCounts(res_status.data)

                  // If no calibrated cameras, stop/cancel so phones don't keep recording.
                  if (this.n_calibrated_cameras === 0) {
                    await axiosGetWithRetry(`/sessions/${this.session.id}/stop/`, {}, { retries: 2, backoffFactor: 0.25, maxJitterMs: 100, timeout: 5000 })
                    await axiosGetWithRetry(`/sessions/${this.session.id}/cancel_trial/`, {}, { retries: 2, backoffFactor: 0.25, maxJitterMs: 100, timeout: 5000 })
                    this.cancelPoll()
                    this.cancelRecordingStatusPoll()
                    this.state = 'ready'
                    this.trialInProcess.status = "error"
                    throw new Error("There are no calibrated cameras for this trial.")
                  }

                  // Transition to recording state
                  this.state = 'recording';

                  // If too many cameras are connected, warn but allow the trial to continue.
                  if (this.n_cameras_connected > this.n_calibrated_cameras) {
                      this.showExtraCameraWarning()
                  }

                  // Check if the appropriate number of cameras is connected.
                  const startTime = Date.now();
                  while (this.n_cameras_connected < this.n_calibrated_cameras) {
                      if (Date.now() - startTime > 5000) { // 5-second timeout
                          await axiosGetWithRetry(`/sessions/${this.session.id}/stop/`, {}, { retries: 2, backoffFactor: 0.25, maxJitterMs: 100, timeout: 5000 })
                          await axiosGetWithRetry(`/sessions/${this.session.id}/cancel_trial/`, {}, { retries: 2, backoffFactor: 0.25, maxJitterMs: 100, timeout: 5000 })
                          this.cancelPoll()
                          this.cancelRecordingStatusPoll()
                          this.state = 'ready'
                          this.trialInProcess.status = "error"
                          const timeoutMsg = (this.n_calibrated_cameras === 1 && this.n_cameras_connected === 0)
                                  ? "No camera connected. Please connect 1 camera to start recording."
                                  : `Expected ${this.n_calibrated_cameras} camera${this.n_calibrated_cameras === 1 ? '' : 's'} but ${this.n_cameras_connected} connected. Please connect the required cameras to start recording.`
                          throw new Error(timeoutMsg)
                      }

                      // Retry fetching the status
                      await new Promise(r => setTimeout(r, 500)); // Wait before retrying
                      const retryRes = await axiosGetWithRetry(`/sessions/${this.session.id}/status/`, {}, { retries: 1, backoffFactor: 0.2, maxJitterMs: 100, timeout: 2000 });
                      this.applyStatusCounts(retryRes.data)
                      if (this.n_cameras_connected > this.n_calibrated_cameras) {
                        this.showExtraCameraWarning()
                      }
                  }

                  if (this.n_cameras_connected <= this.n_calibrated_cameras) {
                    this.sessionNotification = { show: false, text: '', type: 'error' }
                  }

                  // Start recording timer.
                  this.recordingStarted = moment()
                  this.recordingTimePassed = 0
                  this.recordingTimer = window.setTimeout(this.recordTimerHandler, 500)
                  this.startRecordingStatusPoll()

                  // Play sound indicating the subject can start motion.
                  if (this.isAuditoryFeedbackEnabled)
                    playRecordingSound()
                  enforceMinLock = true
                } catch (error) {
                  apiError(error)
                }
    
                this.busy = false
              }
    
              break
            }
            case 'recording': {
              this.busy = true
              this.cancelRecordTimer()
              this.cancelRecordingStatusPoll()

              try {
                const res = await axiosGetWithRetry(`/sessions/${this.session.id}/stop/`, {}, { retries: 2, backoffFactor: 0.25, maxJitterMs: 100, timeout: 5000 })

                // Play sound indicating the subject can stop motion.
                if (this.isAuditoryFeedbackEnabled)
                  playRecordingFinishedSound();

                this.trialInProcess.status = res.data.status
                this.state = 'processing'
                enforceMinLock = true
    
                this.startPoll()
              } catch (error) {
                apiError(error)
              } finally {
                this.busy = false
              }
    
              break
            }
            case 'processing': {
              this.busy = true
              try {
                await axiosGetWithRetry(`/sessions/${this.session.id}/cancel_trial/`, {}, { retries: 2, backoffFactor: 0.25, maxJitterMs: 100, timeout: 5000 })
                this.cancelPoll()
                this.state = 'ready'
                enforceMinLock = true
              } catch (error) {
                apiError(error)
              } finally {
                this.busy = false
              }
              break
            }
          }

          if (enforceMinLock) {
            await this.waitForRecordingActionMinLock(actionStartedAt, minLockDuration)
          }
        } finally {
          this.recordingActionInFlight = false
        }
      },
      recordTimerHandler() {
        this.recordingTimePassed = moment().diff(this.recordingStarted, 'seconds')
  
        if (this.recordingTimeLimit() < 0 || this.recordingTimePassed < this.recordingTimeLimit()) {
          this.recordingTimer = window.setTimeout(this.recordTimerHandler, 500)
        } else {
          apiSuccess('Recording finished upon reaching time limit')
          this.changeState()
        }
      },
      cancelRecordTimer() {
        if (this.recordingTimer) {
          window.clearTimeout(this.recordingTimer)
          this.recordingTimer = null
        }
      },
      startRecordingStatusPoll() {
        this.cancelRecordingStatusPoll()
        const poll = async () => {
          if (this.state !== 'recording') return
          try {
            const res = await axios.get(`/sessions/${this.session.id}/status/`)
            this.applyStatusCounts(res.data)

            if (this.n_cameras_connected > this.n_calibrated_cameras) {
              this.showExtraCameraWarning()
            }
          } catch (e) {
            // Ignore poll errors
          }
          if (this.state === 'recording') {
            this.recordingStatusPoll = window.setTimeout(poll, 2000)
          }
        }
        this.recordingStatusPoll = window.setTimeout(poll, 2000)
      },
      cancelRecordingStatusPoll() {
        if (this.recordingStatusPoll) {
          window.clearTimeout(this.recordingStatusPoll)
          this.recordingStatusPoll = null
        }
      },
      startLidarCooldownTicker() {
        this.lidarCooldownNow = Date.now()
        if (this.lidarCooldownTimer) return
        if (!this.lidarSwitchCooldownUntil || this.lidarSwitchCooldownUntil <= this.lidarCooldownNow) return
        this.lidarCooldownTimer = window.setInterval(() => {
          this.lidarCooldownNow = Date.now()
          if (this.lidarCooldownNow >= this.lidarSwitchCooldownUntil) {
            this.stopLidarCooldownTicker()
          }
        }, 250)
      },
      stopLidarCooldownTicker() {
        if (this.lidarCooldownTimer) {
          window.clearInterval(this.lidarCooldownTimer)
          this.lidarCooldownTimer = null
        }
      },
      extraCameraWarningText() {
        const connectedLabel = `${this.n_cameras_connected} camera${this.n_cameras_connected === 1 ? '' : 's'}`
        const calibratedLabel = `${this.n_calibrated_cameras} calibrated camera${this.n_calibrated_cameras === 1 ? '' : 's'}`
        return `${connectedLabel} recording, but this session has ${calibratedLabel}. The trial will continue.`
      },
      showExtraCameraWarning() {
        this.sessionNotification = { show: true, text: this.extraCameraWarningText(), type: 'warning' }
      },
      async onDownloadData() {
        this.downloading = true
  
        try {
          //console.log(`${axios.defaults.baseURL}sessions/${this.session.id}/download/`)
          let link = document.createElement('a')
          link.setAttribute('href', `${axios.defaults.baseURL}sessions/${this.session.id}/download/`)
          link.setAttribute('download', 'mobilecap_result.zip')
          // This method works in all browsers including FireFox
          link.dispatchEvent(new MouseEvent('click'))
  
          window.setTimeout(() => {
            this.downloading = false
          }, 5000)
        } catch (error) {
          apiError(error)
          this.downloading = false
        }
      },
      async downloadSessionArchive(id) {
        this.isArchiveInProgress = true;
        this.isArchiveDone = false;
        let state = this;
        const startArchiveDownloadUrl = new URL(`/sessions/${id}/async-download/`, axios.defaults.baseURL);
        await axios.get(startArchiveDownloadUrl).then(
            async function pollArchiveOnReady(data) {
              const taskID = data.data.task_id;
              const downloadArchiveOnReadyURL = new URL(`/logs/${taskID}/on-ready/`, axios.defaults.baseURL);
              const response = await axios.get(downloadArchiveOnReadyURL);
              if (response.status === 202) {
                setTimeout(function () {
                  pollArchiveOnReady(data);
                }, 1000);
              }
              if (response.status === 200) {
                clearTimeout(pollArchiveOnReady);
                state.archiveUrl = response.data.url;
                state.isArchiveInProgress = false;
                state.isArchiveDone = true;
              }
            });
      },
      async analysisFunctionsPolls() {
        console.log(this.analysisFunctions);
        for (let func of this.analysisFunctions) {
          for (let trial_id of func.trials) {
            this.checkAnalysisFunction(func.id, trial_id);
          }
        }
      },
      async checkAnalysisFunction(functionId, trial_id) {
        const checkAnalysisFunctionUrl =
            new URL(`/analysis-functions/${functionId}/task-for-trial/${trial_id}/`, axios.defaults.baseURL);
        const state = this;
        await axios.get(checkAnalysisFunctionUrl).then(
            async function pollResultOnReady(data) {
              const taskID = data.data.task_id;
              const getResultOnReadyURL = new URL(`/analysis-result/${taskID}/`, axios.defaults.baseURL);
              const response = await axios.get(getResultOnReadyURL);
              if (response.status === 202) {
                setTimeout(function () {
                  pollResultOnReady(data);
                }, 1000);
              }
              if (response.status === 200) {
                console.log("Analysis result:", response.data)
                clearTimeout(pollResultOnReady);
                state.removeAnalysisFunctionTrial({functionId, trialId: trial_id});
                state.setAnalysisFunctionResult(functionId, response.data);
                state.setAnalysisFunctionState(
                    {functionId, trialId: trial_id, data: {"state": response.data.state, "task_id": taskID}});
              }
            }
        )
      },
      async invokeAnalysisFunction(functionId, trial_id, trial_name) {
        console.log(['invokeAnalysisFunction', functionId, trial_id, trial_name])
        this.setAnalysisFunctionTrial({functionId, trialId: trial_id});
        const state = this;
        const invokeAnalysisFunctionUrl = new URL(`/analysis-functions/${functionId}/invoke/`, axios.defaults.baseURL);
        const invokeData = {session_id: this.session.id, specific_trial_names: [trial_name]};
        await axios.post(invokeAnalysisFunctionUrl, invokeData).then(
            async function pollResultOnReady(data) {
              const taskID = data.data.task_id;
              const getResultOnReadyURL = new URL(`/analysis-result/${taskID}/`, axios.defaults.baseURL);
              const response = await axios.get(getResultOnReadyURL);
              if (response.status === 202) {
                setTimeout(function () {
                  pollResultOnReady(data);
                }, 1000);
              }
              if (response.status === 200) {
                console.log("Analysis result:", response.data)
                clearTimeout(pollResultOnReady);
                state.removeAnalysisFunctionTrial({functionId, trialId: trial_id});
                state.setAnalysisFunctionResult(functionId, response.data);
                state.setAnalysisFunctionState(
                    {
                      functionId, trialId: trial_id,
                      data: {
                        "state": response.data.state,
                        "menu": response.data.menu,
                        "task_id": taskID,
                        "dashboard_id": response.data.dashboard_id
                      }
                    });
                state.loadTrialResults(trial_id);
              }
            });
      },
      newSession() {
        this.clearAll()
        this.$router.push({name: 'RecordingMode'})
      },
      openNewSessionConfirm() {
        this.new_session_confirm_dialog = true
      },
      confirmNewSession() {
        this.new_session_confirm_dialog = false
        this.newSession()
      },
      openNewSessionSameSetupConfirm() {
        this.new_session_same_setup_confirm_dialog = true
      },
      confirmNewSessionSameSetup() {
        this.new_session_same_setup_confirm_dialog = false
        this.newSessionSameSetup()
      },
      openInApp() {
        if (this.sessionDeepLinkUrl) {
          window.location.href = this.sessionDeepLinkUrl
        }
      },
      getSameDeviceSessionStore() {
        if (typeof localStorage === 'undefined') return {}
        try {
          const raw = localStorage.getItem('opencap_same_device_sessions')
          if (!raw) return {}
          const parsed = JSON.parse(raw)
          return parsed && typeof parsed === 'object' ? parsed : {}
        } catch (error) {
          return {}
        }
      },
      setSameDeviceSessionStore(store) {
        if (typeof localStorage === 'undefined') return
        localStorage.setItem('opencap_same_device_sessions', JSON.stringify(store))
      },
      isSessionMarkedSameDevice(sessionId) {
        if (!sessionId) return false
        const store = this.getSameDeviceSessionStore()
        return store[sessionId] === true
      },
      persistSameDeviceSessionFlag() {
        if (this.$route.query.sameDevice !== 'true' || !this.session?.id) return
        const store = this.getSameDeviceSessionStore()
        store[this.session.id] = true
        this.setSameDeviceSessionStore(store)
      },
      getAdvancedSettingsMetadataParams(settings = {}) {
        const params = {}
        const settingKeys = [
          { storedKey: 'scalingsetup', paramKey: 'settings_scaling_setup' },
          { storedKey: 'posemodel', paramKey: 'settings_pose_model' },
          { storedKey: 'framerate', paramKey: 'settings_framerate' },
          { storedKey: 'openSimModel', paramKey: 'settings_openSimModel' },
          { storedKey: 'augmentermodel', paramKey: 'settings_augmenter_model' },
          { storedKey: 'filterfrequency', paramKey: 'settings_filter_frequency' },
          { storedKey: 'sync_ver', paramKey: 'settings_synchronization_version' },
        ]

        settingKeys.forEach(({ storedKey, paramKey }) => {
          if (settings[storedKey] !== undefined && settings[storedKey] !== null && settings[storedKey] !== '') {
            params[paramKey] = settings[storedKey]
          }
        })

        return params
      },
      setPublic(p) {
        console.log(p)
        axios.patch(`/sessions/${this.session.id}/`, {"public": p})
      },
      async copySessionUrlToClipboard() {
        if (!this.sessionUrl) return
        try {
          await navigator.clipboard.writeText(this.sessionUrl)
          this.sessionNotification = { show: true, text: 'Link copied to clipboard!', type: 'success' }
        } catch {
          try {
            const input = document.createElement('input')
            input.value = this.sessionUrl
            input.setAttribute('readonly', '')
            input.style.position = 'absolute'
            input.style.left = '-9999px'
            document.body.appendChild(input)
            input.select()
            document.execCommand('copy')
            document.body.removeChild(input)
            this.sessionNotification = { show: true, text: 'Link copied to clipboard!', type: 'success' }
          } catch {
            this.sessionNotification = { show: true, text: 'Failed to copy link', type: 'error' }
          }
        }
      },
      async newSessionSameSetup() {
        // Snapshot before initSessionSameSetup: new_subject response may omit isMono on the new session.
        const wasMonocular = !!(this.session?.isMono ?? this.session?.is_mono)
        const wasSameDevice = this.isSameDevice
        const advancedSettings = this.getAdvancedSettingsMetadataParams(this.session?.meta?.settings)
        await this.initSessionSameSetup()
        if (!wasMonocular && Object.keys(advancedSettings).length > 0) {
          await axios.get(`/sessions/${this.session.id}/set_metadata/`, { params: advancedSettings })
        }
        const query = wasMonocular
          ? {
            isMono: 'true',
            ...(wasSameDevice ? { fromDevice: 'true' } : {})
          }
          : {}
        this.$router.push({name: 'Neutral', params: {id: this.session.id}, query})
      },
      startPoll() {
        this.cancelPoll()
        this.statusPoll = window.setTimeout(async () => {
          const res = await axiosGetWithRetry(`/sessions/${this.session.id}/status/`)
          this.applyStatusCounts(res.data)
  
          if (res.data.status !== 'uploading') {
            // Show error if any
            if (res.data.status === 'error') {
              apiErrorRes(res.data, 'Finished with error')
            }
            if (res.data.status === 'processing' || res.data.status === 'ready') {
              if (this.n_cameras_connected !== this.n_calibrated_cameras) {
                if (this.n_cameras_connected > this.n_calibrated_cameras) {
                  this.showExtraCameraWarning()
                } else {
                  const num_missing_cameras = this.n_calibrated_cameras - this.n_videos_uploaded
                  const expectedLabel = this.pluralize(this.n_calibrated_cameras, 'camera', 'cameras')
                  const missingLabel = this.pluralize(num_missing_cameras, 'camera', 'cameras')
                  apiErrorRes(res.data, `${this.n_calibrated_cameras} ${expectedLabel} expected and ${this.transferProgressDescription}. Please reconnect the missing ${num_missing_cameras} ${missingLabel} to the session using the QR code at the top of the screen.`);
                }
              }
            }
            this.state = 'ready'
          } else {
            this.startPoll()
          }
        }, 2000)
      },
      cancelPoll() {
        if (this.statusPoll) {
          window.clearTimeout(this.statusPoll)
          this.statusPoll = null
        }
      },
      startTrialsPoll() {
        if (this.trialsPollActive) return

        this.trialsPollActive = true
        const poll = async () => {
          this.trialsPoll = null
          const sessionId = this.session?.id
          if (!this.trialsPollActive || !sessionId) return

          try {
            const res = await axios.get(`/sessions/${sessionId}/status/?ret_session=true`)
            if (!this.trialsPollActive || this.session?.id !== sessionId) return

            const updatedTrials = res.data.session.trials || []

            // Add new trials and update existing ones
            updatedTrials.forEach(updatedT => {
              const existingIndex = this.session.trials.findIndex(t => t.id === updatedT.id)
              if (existingIndex < 0) {
                this.addTrial(updatedT)
              } else {
                const existing = this.session.trials[existingIndex]
                const statusChanged = existing.status !== updatedT.status
                // Videos can flip to saved_local after the status settles (e.g. local
                // saving on phone), so refresh when that state changes too.
                const savedLocalChanged = this.isTrialSavedLocally(existing) !== this.isTrialSavedLocally(updatedT)
                if (statusChanged || savedLocalChanged) {
                  this.updateTrial(updatedT)
                }
              }
            })
          } catch (e) {
            // Ignore poll errors (e.g. session no longer exists)
          }

          if (this.trialsPollActive && this.session?.id === sessionId) {
            this.trialsPoll = window.setTimeout(poll, 5000)
          }
        }

        this.trialsPoll = window.setTimeout(poll, 5000)
      },
      cancelTrialsPoll() {
        this.trialsPollActive = false
        if (this.trialsPoll) {
          window.clearTimeout(this.trialsPoll)
          this.trialsPoll = null
        }
      },
      trialClasses(trial) {
        const classes = [trial.trashed ? 'trashed' : 'cursor-pointer'];
        if (this.isTrialSavedLocally(trial)) {
          classes.push('trial-saved-local');
        }
        return classes;
      },
      isTrialSavedLocally(trial) {
        const videos = trial?.videos;
        if (!Array.isArray(videos) || videos.length === 0) return false;
        const isTruthy = raw => raw === true || ['true', '1', 'yes', 'on'].includes(String(raw).toLowerCase());
        return videos.every(v => v && isTruthy(v.saved_local));
      },
      clickOutsideDialogTrialHideMenu(e) {
        if (e.target.className === 'v-overlay__scrim') {
          for (let t of this.filteredTrialsWithMenu) {
            t.isMenuOpen = false;
          }
          this.showTrialMenuSheet = false;
          this.selectedTrialForMenu = null;
        }
      },
      openTrialMenuSheet(trial) {
        this.selectedTrialForMenu = trial;
        this.showTrialMenuSheet = true;
      },
      closeMenuAndRename(t) { t.isMenuOpen = false; this.renameTrialDialog(t); },
      closeMenuAndAnalysis(t) { t.isMenuOpen = false; this.analysisDialog(t); },
      closeMenuAndEditTags(t) { t.isMenuOpen = false; this.addTagTrialDialog(t); },
      closeMenuAndOpenTrashDialog(t) { t.isMenuOpen = false; this.trialForTrashDialog = t; this.remove_dialog = true; },
      closeMenuAndOpenRestoreDialog(t) { t.isMenuOpen = false; this.trialForRestoreDialog = t; this.restore_dialog = true; },
      closeMenuAndOpenDeleteDialog(t) { t.isMenuOpen = false; this.trialForPermanentDeleteDialog = t; this.permanent_delete_dialog = true; },
      closeSheetAndRename(t) { this.showTrialMenuSheet = false; this.selectedTrialForMenu = null; this.renameTrialDialog(t); },
      closeSheetAndAnalysis(t) { this.showTrialMenuSheet = false; this.selectedTrialForMenu = null; this.analysisDialog(t); },
      closeSheetAndEditTags(t) { this.showTrialMenuSheet = false; this.selectedTrialForMenu = null; this.addTagTrialDialog(t); },
      closeSheetAndOpenTrashDialog(t) { this.showTrialMenuSheet = false; this.selectedTrialForMenu = null; this.trialForTrashDialog = t; this.remove_dialog = true; },
      closeSheetAndOpenRestoreDialog(t) { this.showTrialMenuSheet = false; this.selectedTrialForMenu = null; this.trialForRestoreDialog = t; this.restore_dialog = true; },
      closeSheetAndOpenDeleteDialog(t) { this.showTrialMenuSheet = false; this.selectedTrialForMenu = null; this.trialForPermanentDeleteDialog = t; this.permanent_delete_dialog = true; },
      closeTrashDialog() {
        this.trialForTrashDialog = null;
        this.remove_dialog = false;
        for (let t of this.filteredTrialsWithMenu) t.isMenuOpen = false;
      },
      closeRestoreDialog() {
        this.trialForRestoreDialog = null;
        this.restore_dialog = false;
        for (let t of this.filteredTrialsWithMenu) t.isMenuOpen = false;
      },
      closePermanentDeleteDialog() {
        this.trialForPermanentDeleteDialog = null;
        this.permanent_delete_dialog = false;
        for (let t of this.filteredTrialsWithMenu) t.isMenuOpen = false;
      },
      confirmTrashTrial() {
        if (this.trialForTrashDialog) {
          this.trashTrial(this.trialForTrashDialog);
          this.closeTrashDialog();
        }
      },
      confirmRestoreTrial() {
        if (this.trialForRestoreDialog) {
          this.restoreTrial(this.trialForRestoreDialog);
          this.closeRestoreDialog();
        }
      },
      confirmPermanentDeleteTrial() {
        if (this.trialForPermanentDeleteDialog) {
          this.permanentDeleteTrial(this.trialForPermanentDeleteDialog);
          this.closePermanentDeleteDialog();
        }
      },
  
      async analysisDialog(trial) {
        const index = this.session.trials.findIndex(x => x.id === trial.id)
        this.trial_analysis_index = index;
        this.showAnalysisDialog = true;
      },
  
      openSessionRenameDialog() {
        this.sessionNewName = this.displaySessionName;
        this.session_rename_dialog = true;
        this.$nextTick(() => this.$refs.observer_session_rename?.reset());
      },
      submitRenameSession() {
        this.$refs.observer_session_rename?.validate().then(valid => {
          if (valid) {
            this.session_rename_dialog = false;
            this.renameSession(this.sessionNewName.trim());
          }
        });
      },
      async renameSession(sessionNewName) {
        try {
          const { data } = await axios.post(`/sessions/${this.session.id}/rename/`, { sessionNewName });
          const updated = data?.data != null ? data.data : data;
          if (updated) this.setSession(updated);
          this.sessionNotification = { show: true, text: 'Session renamed successfully.', type: 'success' };
        } catch (error) {
          apiError(error);
        }
      },
      async renameTrialDialog(trial) {
        const index = this.session.trials.findIndex(x => x.id === trial.id)
        this.trial_rename_index = index;
        this.trialNewName = trial.name;
        this.trial_rename_dialog = true;
      },
      submitRenameTrial() {
        const trial = this.session.trials[this.trial_rename_index];
        if (this.state !== 'ready' || trial?.status === 'processing' || trial?.status === 'uploading') return;
        this.$refs.observer_tr.validate().then(valid => {
          if (valid) {
            this.trial_rename_dialog = false;
            this.renameTrial(trial, this.trial_rename_index, this.trialNewName);
          }
        });
      },
      async addTagTrialDialog(trial) {
        const index = this.session.trials.findIndex(x => x.id === trial.id)
        this.trial_modify_tags_index = index;
        this.trial_modify_tags = true;
        // Load trial tags
        const res = await axios.get(`/trial-tags/${trial.id}/get_tags_trial/`)
        this.trialNewTags = []
        res.data.forEach((tag) => {
            this.trialNewTags.push(tag.tag)
        });
        console.log(res.data)
        console.log(trial.id)
      },
      async renameTrial(trial, index, trialNewName) {
        try {
          let oldName = trial.name
          console.log(trial.name + " will be renamed to " + trialNewName);
          const {data} = await axios.post(`/trials/${trial.id}/rename/`, {trialNewName});
          await this.updateTrialWithData(trial, data.data);
          this.sessionNotification = { show: true, text: 'Trial renamed successfully.', type: 'success' };
        } catch (error) {
          apiError(error)
        }
      },
      async modifyTagsTrial(trial, index, trialNewTags) {
        try {
          let oldTags = trial.tags
          console.log(trial.tags + " will be replaced by " + trialNewTags)
          const {data} = await axios.post(`/trials/${trial.id}/modifyTags/`, {trialNewTags});
          await this.updateTrialWithData(trial, data.data);
          this.sessionNotification = { show: true, text: 'Trial tags updated.', type: 'success' };
        } catch (error) {
          apiError(error)
        }
      },
      async updateTrialWithData(trial, data) {
        const index = this.session.trials.findIndex(x => x.id === trial.id)
        if (index >= 0) {
          if (Object.keys(data).length === 0) {
            // if permanent remove was done
            Vue.delete(this.session.trials, index);
          } else {
            Vue.set(this.session.trials, index, data);
          }
        }
      },
      async trashTrial(trial) {
        try {
          const {data} = await axios.post(`/trials/${trial.id}/trash/`);
          await this.updateTrialWithData(trial, data);
          this.sessionNotification = { show: true, text: 'Trial moved to trash.', type: 'success' };
        } catch (error) {
          apiError(error)
        }
      },
      async permanentDeleteTrial(trial) {
        try {
          await axios.post(`/trials/${trial.id}/permanent_remove/`);
          await this.updateTrialWithData(trial, {});
          this.sessionNotification = { show: true, text: 'Trial permanently deleted.', type: 'success' };
        } catch (error) {
          apiError(error);
        }
      },
      async restoreTrial(trial) {
        try {
          const {data} = await axios.post(`/trials/${trial.id}/restore/`);
          await this.updateTrialWithData(trial, data);
          this.sessionNotification = { show: true, text: 'Trial restored.', type: 'success' };
        } catch (error) {
          apiError(error)
        }
      },
      async loadTrialResults(trial_id) {
        const {data} = await axios.get(`/trials/${trial_id}/`)
        for (let i = 0; i < this.session.trials.length; i++) {
          if (this.session.trials[i].id === trial_id) {
            Vue.set(this.session.trials[i], 'results', data.results)
          }
        }
      },
      disposeScene() {
        this.stopRenderLoop()
        if (this.controls) {
          this.controls.dispose()
          this.controls = null
        }
        if (this.renderer) {
          this.renderer.dispose()
          this.renderer = null
        }
        if (this.scene) {
          this.scene.traverse((object) => {
            if (object.isMesh) {
              if (object.geometry) {
                object.geometry.dispose();
              }
              if (object.material) {
                if (Array.isArray(object.material)) {
                  object.material.forEach(m => m.dispose());
                } else {
                  object.material.dispose();
                }
              }
            }
          });

          // Clear meshes cache
          for (let key in this.meshes) {
            if (this.meshes[key]) {
              if (this.meshes[key].geometry) {
                this.meshes[key].geometry.dispose();
              }
              if (this.meshes[key].material) {
                if (Array.isArray(this.meshes[key].material)) {
                  this.meshes[key].material.forEach(m => m.dispose());
                } else {
                  this.meshes[key].material.dispose();
                }
              }
            }
          }
          this.meshes = {};
        }
      },
      checkPerformance() {
        if (typeof window !== 'undefined' && window.performance) {
          // Check if device is low performance
          const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
          const memory = navigator.deviceMemory || 4;
          const cores = navigator.hardwareConcurrency || 4;

          this.isLowPerformance = isMobile || memory < 4 || cores < 4;

          if (this.isLowPerformance) {
            this.renderQuality = 'medium';
            this.maxFrameSkip = 2;
            console.log('Low performance mode enabled');
          }
        }
      },
      async loadTrial(trial) {
        console.log('loadTrial')
        // Clear any previous toast/errors when switching trials.
        // For "error" trials, Status.vue shows the toast before it triggers this load, so don't clear here.
        if (trial?.status !== 'error') {
          clearToastMessages()
        }
        this.sessionNotification = { show: false, text: '', type: 'error' }
        this.time = 0
        this.cameraView = 'default'
        this.followSubject = false
        this.followSmoothedLookAt = null

        if (!this.trialLoading) {
          this.frame = 0
          this.trial = null
          this.videos = []
          this.synced = false
          this.trialLoading = true
          this.sceneReady = false
          this.stopRenderLoop()
          this.togglePlay(false)

          try {
            // Check cache first
            let data;
            const cacheKey = `trial_${trial.id}`;
            if (this.dataCache.has(cacheKey)) {
              console.log('Loading trial from cache');
              data = this.dataCache.get(cacheKey);
            } else {
              const response = await axios.get(`/trials/${trial.id}/`)
              data = response.data;
              this.dataCache.set(cacheKey, data);
            }

            this.trial = data
            console.log("Trial:", data)
  
            // load JSON
            const json = data.results.filter(element => element.tag == "visualizerTransforms-json")
            if (json && json.length > 0) {
              let jsonData
              const url = json[0].media
  
              if (url.startsWith(axios.defaults.baseURL)) {
                const res = await axios.get(url)
                jsonData = res.data
              } else {
                let axiosClean = axios.create()
  
                const res = await axiosClean.get(url, {
                  // Deleting Authorization header, because we have one as global Axios
                  // Do not pass out user token to 3rd party sites
                  transformRequest: [(data, headers) => {
                    delete headers.common.Authorization
                    return data
                  }]
                })

                jsonData = res.data
              }

              this.frames = jsonData.time
              this.animation_json = jsonData
            } else {
              this.frames = [] //null
            }
  
            this.videos = data.results.filter(element => element.tag == "video-sync")

            let render_skeleton = true

            if (this.videos.length === 0) {
              this.frame = 0
              this.time = 0
              // IMPORTANT: Don't blindly fall back to `data.videos` because it may not be trial-scoped
              // (e.g., session-level videos). Only use it if each entry explicitly references this trial id.
              const fallback = Array.isArray(data?.videos) ? data.videos : []
              const trialId = data?.id
              const scopedFallback = fallback.filter(v => {
                const ref =
                  v?.trial_id ?? v?.trialId ?? v?.trialID ?? v?.trial ?? v?.trial_pk ?? v?.trialPk
                return trialId != null && ref != null && String(ref) === String(trialId)
              })

              if (scopedFallback.length > 0) {
                this.videos = scopedFallback.map(v => ({
                  ...v,
                  media: v.media ?? v.video,
                }))
              } else if (fallback.length > 0) {
                this.videos = []
                this.sessionNotification = {
                  show: true,
                  type: 'warning',
                  text: 'This trial has videos but no synced previews available (likely QC/low-confidence). Not showing unscoped videos to avoid mixing trials.',
                }
              }

              render_skeleton = false
            }

            // Only initialize the Three.js 3D scene when we have motion frames to render.
            // When there are only videos (or no data), keep the viewer empty-state and let users preview videos.
            if (this.frames.length > 0) {
              this.$nextTick(() => {
                try {
                  // Dispose old scene
                  this.disposeScene();

                  while (this.$refs.mocap.lastChild) {
                    this.$refs.mocap.removeChild(this.$refs.mocap.lastChild)
                  }

                  // setup3d
                  const container = this.$refs.mocap
  
                  const ratio = container.clientHeight > 0
                    ? container.clientWidth / container.clientHeight
                    : 16 / 9
                  this.camera = new THREE.PerspectiveCamera(45, ratio, 0.1, 125)
                  this.camera.position.x = 4.5
                  this.camera.position.z = -3
                  this.camera.position.y = 3

                  this.scene = new THREE.Scene()
                  this.renderer = new THREE.WebGLRenderer({
                    antialias: true,
                    powerPreference: "high-performance"
                  })
                  this.renderer.setClearColor(this.sceneBackgroundColor())
                  this.renderer.shadowMap.enabled = true;

                  // Adaptive render quality
                  const pixelRatio = this.isLowPerformance ?
                    Math.min(window.devicePixelRatio, 1.5) :
                    window.devicePixelRatio;
                  this.renderer.setPixelRatio(pixelRatio)

                  // Reduce shadow quality on mobile
                  if (this.isLowPerformance) {
                    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
                  }

                  this.onResize()
                  container.appendChild(this.renderer.domElement)
                  this.controls = new THREE_OC.OrbitControls(this.camera, this.renderer.domElement)
                  this.controls.target.set(0, 1, 0)
                  this.controls.update()

                  // show3d
                  // add the plane - with cached texture
                  {
                    const planeSize = 12;
  
                    const loader = new THREE.TextureLoader();
                    // Use cached texture if available
                    let texture;
                    const textureKey = 'checker_texture';
                    if (this.materialCache.has(textureKey)) {
                      texture = this.materialCache.get(textureKey);
                    } else {
                      texture = loader.load('https://threejsfundamentals.org/threejs/resources/images/checker.png');
                      this.materialCache.set(textureKey, texture);
                    }

                    texture.wrapS = THREE.RepeatWrapping;
                    texture.wrapT = THREE.RepeatWrapping;
                    texture.magFilter = THREE.NearestFilter;
                    const repeats = planeSize*1.5;
                    texture.repeat.set(repeats, repeats);
  
                    const planeGeo = new THREE.PlaneGeometry(planeSize, planeSize);
                    const planeMat = new THREE.MeshPhongMaterial({
                      map: texture,
                      side: THREE.DoubleSide,
                      transparent: true,
                      opacity: 0.5,
                    });
                    const mesh = new THREE.Mesh(planeGeo, planeMat);
                    mesh.rotation.x = Math.PI * -.5;
                    mesh.position.y = .0
                    mesh.receiveShadow = true;
                    this.scene.add(mesh);
                  }
  
                  // add sun
                  {
                    const skyColor = 0xB1E1FF;  // light blue
                    const groundColor = 0xB97A20;  // brownish orange
                    const intensity = 0.5
                    const light = new THREE.HemisphereLight(skyColor, groundColor, intensity);
                    this.scene.add(light);
                  }
  
                  // add directional light - with reduced shadow resolution for performance
                  {
                    const color = 0xFFFFFF;
                    const intensity = 0.8;
                    const light = new THREE.DirectionalLight(color, intensity);
                    light.position.set(2, 3, 1.5);
                    light.target.position.set(0, 0, 0);
                    light.castShadow = true;
                    light.shadow.camera.left = -50;
                    light.shadow.camera.right = 50;
                    light.shadow.camera.top = 50;
                    light.shadow.camera.bottom = -50;
                    light.shadow.camera.near = 0.1;
                    light.shadow.camera.far = 200;
                    light.shadow.camera.zoom = 16

                    // Reduced shadow map size for low performance devices
                    const shadowSize = this.isLowPerformance ? 1024 : 2048;
                    light.shadow.mapSize.width = shadowSize;
                    light.shadow.mapSize.height = shadowSize;
                    this.scene.add(light);
                    this.scene.add(light.target);
  
                    // const helper = new THREE.DirectionalLightHelper(light);
                    // this.scene.add(helper);
  
                    // const cameraHelper = new THREE.CameraHelper(light.shadow.camera);
                    // this.scene.add(cameraHelper);
                  }

                  if (render_skeleton) {
                    // add bones
                    for (let body in this.animation_json.bodies) {
                      let bd = this.animation_json.bodies[body]
                      bd.attachedGeometries.forEach((geom) => {
                        let path = 'https://mc-opencap-public.s3.us-west-2.amazonaws.com/geometries/' + geom.substr(0, geom.length - 4) + ".obj";
                        objLoader.load(path, (root) => {
                          root.castShadow = true;
                          root.receiveShadow = true;
                          root.traverse(function (child) {
                            if (child instanceof THREE.Mesh) {
                              //                               child.receiveShadow = true;
                              child.castShadow = true;
                            }
                          });
                          this.meshes[body + geom] = root;
                          this.meshes[body + geom].scale.set(bd.scaleFactors[0], bd.scaleFactors[1], bd.scaleFactors[2])
                          this.scene.add(root);
                        })
                      })
                    }
                  }
                } finally {
                  this.trialLoading = false
                }
  
                this.onResize()
                this.initResizeObserver()

                // animate
  
                function delay(time) {
                  return new Promise(resolve => setTimeout(resolve, time));
                }

                let timeout = 2000
                if (navigator.connection) {
                  console.log('supported: connection', navigator.connection.downlink)
                  timeout = Math.trunc(10000 / navigator.connection.downlink)
                }

                delay(timeout).then(() => {
                  this.sceneReady = true
                  // Keep the render loop running so OrbitControls work while paused.
                  this.startRenderLoop()
                  // For neutral trials, leave videos paused at frame 0.
                  this.togglePlay(true)
                });
              })
            }

            this.trialLoading = false  // Fix to unstuck trial loading in case of an issue
          } catch (error) {
            apiError(error)
            this.trialLoading = false
          }
        }
      },
      initResizeObserver() {
        if (typeof ResizeObserver === 'undefined' || !this.$refs.mocap) {
          return
        }
        if (this.resizeObserver) {
          this.resizeObserver.disconnect()
          this.resizeObserver = null
        }
        this.resizeObserver = new ResizeObserver(() => {
          this.onResize()
        })
        this.resizeObserver.observe(this.$refs.mocap)
      },
      onResize() {
        const container = this.$refs.mocap
        if (container && this.renderer) {
          const width = container.clientWidth
          const height = container.clientHeight
          if (!width || !height) {
            return
          }
          // Keep drawing buffer in sync with the visible canvas size.
          this.renderer.setSize(width, height, true)
          if (this.camera) {
            this.camera.aspect = width / height
            this.camera.updateProjectionMatrix()
          }
        }
      },
      sceneBackgroundColor() {
        return this.isDarkTheme ? 0x000000 : 0x808080
      },
      startRenderLoop() {
        if (this.renderLoopActive) return
        this.renderLoopActive = true
        this.animate()
      },
      stopRenderLoop() {
        this.renderLoopActive = false
      },
      animate() {
        if (!this.renderLoopActive || this.trialLoading || !this.renderer) {
          this.renderLoopActive = false
          return
        }

        requestAnimationFrame(this.animate)

        if (this.playing) {
          // Check if enough time has passed for next frame (cap at 60fps)
          const now = performance.now()
          if (now - this.lastFrameTime >= 16) {
            this.lastFrameTime = now
            this.animateOneFrame()
          }
        } else if (this.scene && this.camera) {
          this.renderer.render(this.scene, this.camera)
        }
      },
      animateOneFrame() {
        let cframe = 0
        const frameCount = this.frames.length
        const video = this.vid0()
        const hasValidDuration = video && !isNaN(video.duration) && video.duration > 0

        if (frameCount > 0) {
          if (hasValidDuration && this.videos.length > 0) {
            if (this.playing) {
              const t = video.currentTime

              // Skip frames on low performance devices
              if (this.isLowPerformance && this.frameSkipCounter < this.maxFrameSkip) {
                this.frameSkipCounter++
                if (this.renderer && this.scene && this.camera) {
                  this.renderer.render(this.scene, this.camera)
                }
                return
              }
              this.frameSkipCounter = 0

              cframe = this.frameIndexAtTime(t)
              this.frame = cframe
              this.time = this.formatMotionTime(this.frameTimeAt(cframe))
            } else {
              cframe = Math.min(frameCount - 1, Math.max(0, this.frame))
              this.time = this.formatMotionTime(this.frameTimeAt(cframe))
            }
          } else if (this.videos.length === 0 && this.playing) {
            cframe = this.frame
            this.frame++
            if (this.frame >= frameCount) {
              this.frame = frameCount - 1
            }
          } else {
            cframe = Math.min(frameCount - 1, Math.max(0, this.frame))
            if (this.hasFrameTimestamps()) {
              this.time = this.formatMotionTime(this.frameTimeAt(cframe))
            }
          }

          if (cframe < frameCount) {
            const json = this.animation_json
            for (let body in json.bodies) {
              json.bodies[body].attachedGeometries.forEach((geom) => {
                if (this.meshes[body + geom]) {
                  this.meshes[body + geom].position.set(
                      json.bodies[body].translation[cframe][0],
                      json.bodies[body].translation[cframe][1],
                      json.bodies[body].translation[cframe][2])
                  var euler = new THREE.Euler(
                      json.bodies[body].rotation[cframe][0],
                      json.bodies[body].rotation[cframe][1],
                      json.bodies[body].rotation[cframe][2])
                  this.meshes[body + geom].quaternion.setFromEuler(euler)
                }
              })
            }
          }

          if (this.followSubject) {
            this.updateFollowCamera(cframe)
          }

          if (hasValidDuration) {
            this.syncVideos()
          }
        }

        // Always render the 3D scene regardless of video metadata state.
        // On iOS Safari, video.duration stays NaN until metadata loads, which
        // would otherwise leave the canvas permanently black.
        if (this.renderer && this.scene && this.camera) {
          this.renderer.render(this.scene, this.camera)
        }

        if (this.trial?.name === 'neutral') {
            this.togglePlay(false)
        }
      },
      getSubjectCenter(frameIndex) {
        const pose = this.getPelvisPose(frameIndex)
        return pose ? pose.position.clone() : null
      },
      getPelvisPose(frameIndex) {
        return getPelvisPose(this.animation_json, frameIndex ?? this.frame, this.frames?.length || 0)
      },
      setCameraView(view) {
        if (!this.camera || !this.controls) {
          return
        }

        this.cameraView = view
        const pose = this.getPelvisPose(this.frame) || {
          position: new THREE.Vector3(0, 1, 0),
          anterior: new THREE.Vector3(1, 0, 0),
          superior: new THREE.Vector3(0, 1, 0),
          right: new THREE.Vector3(0, 0, 1),
          quaternion: new THREE.Quaternion()
        }

        placeCameraForView(this.camera, this.controls, view, pose)
        if (this.followSubject) {
          this.lockFollowTransform()
        }

        if (this.renderer && this.scene) {
          this.renderer.render(this.scene, this.camera)
        }
      },
      setFollowSubject(enabled) {
        this.followSubject = !!enabled
        if (this.followSubject) {
          this.lockFollowTransform()
          this.updateFollowCamera(this.frame)
          if (this.renderer && this.scene && this.camera) {
            this.renderer.render(this.scene, this.camera)
          }
        } else {
          this.followSmoothedLookAt = null
        }
      },
      lockFollowTransform() {
        const pose = this.getPelvisPose(this.frame)
        this.followSmoothedLookAt = snapFollowToSubject(this.camera, this.controls, pose)
      },
      updateFollowCamera(frameIndex) {
        if (!this.camera || !this.controls) {
          return
        }

        const pose = this.getPelvisPose(frameIndex)
        if (!pose) {
          return
        }

        if (!this.followSmoothedLookAt) {
          this.lockFollowTransform()
        }

        this.followSmoothedLookAt = followSubjectHorizontally(
          this.camera,
          this.controls,
          pose,
          this.followSmoothedLookAt
        )
      },
      syncVideos() {
        if (this.synced || this.trial == null || this.videos.length == 0)
          return
  
        // will also reset all videos
        this.playSpeed = 1
  
        /*
        this.videos.forEach((video, index) => {
          const vid_element = this.videoElement(index)
          vid_element.playbackRate = 1
        })
        */
  
        this.synced = true
      },
      onVideoEnded(index) {
        if (index === 0) {
          if (this.loopPlayback) {
            const startTime = this.frameTimeAt(0)
            this.frame = 0
            this.videos.forEach((video, index) => {
              const vid_element = this.videoElement(index)
              if (vid_element) {
                vid_element.currentTime = startTime
                vid_element.play()
              }
            })
          } else {
            this.togglePlay(false)
          }
        }
      },
      onVideoLoadedMetadata(index) {
        // On Safari, seeking after metadata loads forces the first frame to paint
        // even when the video is paused (important for neutral trials).
        const el = this.videoElement(index)
        if (el) {
          el.currentTime = this.frameTimeAt(0)
        }
      },
      videoElement(index) {
        const vid = this.$refs[`video-${index}`]
  
        return vid
            ? vid[0]
            : null
      },
      vid0() {
        return this.videoElement(0)
      },
      hasFrameTimestamps() {
        return Array.isArray(this.frames) &&
          this.frames.length > 0 &&
          typeof this.frames[0] === 'number' &&
          !Number.isNaN(this.frames[0])
      },
      frameTimeAt(index) {
        const frameCount = this.frames.length
        if (frameCount === 0) return 0

        const clamped = Math.max(0, Math.min(index, frameCount - 1))
        if (this.hasFrameTimestamps()) {
          return this.frames[clamped]
        }

        const video = this.vid0()
        if (video && !isNaN(video.duration) && video.duration > 0) {
          return clamped * video.duration / frameCount
        }
        return clamped
      },
      frameIndexAtTime(t) {
        const frameCount = this.frames.length
        if (frameCount === 0) return 0

        const time = parseFloat(t)
        if (!Number.isFinite(time)) return 0

        if (this.hasFrameTimestamps()) {
          const times = this.frames
          if (time <= times[0]) return 0
          if (time >= times[frameCount - 1]) return frameCount - 1

          let lo = 0
          let hi = frameCount - 1
          while (lo < hi) {
            const mid = Math.ceil((lo + hi) / 2)
            if (times[mid] <= time) lo = mid
            else hi = mid - 1
          }
          return lo
        }

        const video = this.vid0()
        if (video && !isNaN(video.duration) && video.duration > 0) {
          const frame = Math.round(time * frameCount / video.duration)
          return Math.max(0, Math.min(frame, frameCount - 1))
        }
        return 0
      },
      formatMotionTime(time) {
        const t = Number(time)
        return Number.isFinite(t) ? parseFloat(t.toFixed(2)) : 0
      },
      isSelected(trial) {
        return this.trial && this.trial.id === trial.id
      },
      eachVideo(func) {
        this.videos.forEach((video, index) => {
          func(this.videoElement(index))
        })
      },
      togglePlay(value) {
        this.playing = value

        if (this.playing) {
          this.videos.forEach((video, index) => {
            const vid_element = this.videoElement(index)
            if (vid_element) vid_element.play()
          })
        } else {
          this.videos.forEach((video, index) => {
            const vid_element = this.videoElement(index)
            if (vid_element) vid_element.pause()
          })
        }
      },
      toggleLoopPlayback() {
        this.loopPlayback = !this.loopPlayback
      },
      cycleMobileVideoSize() {
        this.mobileVideoSizeIndex = (this.mobileVideoSizeIndex + 1) % 3
      },
      onNavigate(frame) {
        if (!this.frames.length) return

        const clampedFrame = Math.max(0, Math.min(frame, this.frames.length - 1))
        this.frame = clampedFrame

        const video = this.vid0()
        if (video && !isNaN(video.duration) && video.duration > 0) {
          const newPosition = this.frameTimeAt(clampedFrame)

          this.eachVideo(videoElement => {
            if (videoElement) videoElement.currentTime = newPosition
          })
          this.time = this.formatMotionTime(newPosition)
        } else if (this.hasFrameTimestamps()) {
          this.time = this.formatMotionTime(this.frameTimeAt(clampedFrame))
        }

        this.animateOneFrame()
      },
      onChangeTime(time) {
        const video = this.vid0()
        if (!video) return

        this.eachVideo(videoElement => {
          if (videoElement) videoElement.currentTime = time
        })

        if (!this.playing && this.frames.length > 0) {
          this.frame = this.frameIndexAtTime(time)
        }

        this.animateOneFrame()
      },
      maxVideoDuration() {
        return this.vid0() ? (this.vid0().duration - 1) : 0
      },
      recordingTimeLimit() {
        // Default value is 60.
        const DEFAULT_TIME_LIMIT = 60;
        const VALID_FRAMERATES = [60, 120, 240];

        let timelimit = DEFAULT_TIME_LIMIT;
        const framerate = this.sessionFramerate;

        // Adjust timelimit for valid framerates
        if (framerate !== null && VALID_FRAMERATES.includes(framerate)) {
          timelimit = 60 / (framerate / 60);
        }

        return this.isTesterUser ? timelimit * 5 : timelimit;
      },
      toggleSessionMenuButtons() {
        this.showSessionMenuButtons = !this.showSessionMenuButtons;
      },
      goToAnalysisDashboard(dashboardId, trialId) {
        if (trialId) {
          this.$router.push({
            name: 'AnalysisDashboard',
            params: {id: dashboardId},
            query: {trialId: trialId}
          })
        } else {
          this.$router.push({
            name: 'AnalysisDashboard',
            params: {id: dashboardId}
          })
        }
      },
      requestDownloadMenuItem(trial, menu_item) {
        let tag = menu_item.result_tag;
        for (let i = 0; i < trial.results.length; i++) {
          console.log(trial.results[i].tag);
          if (trial.results[i].tag === tag) {
            let url = trial.results[i].media;
            let link = document.createElement('a')
            link.setAttribute('href', url)
            link.setAttribute('download', tag)
            link.setAttribute('target', '_blank')
            // This method works in all browsers including FireFox
            link.dispatchEvent(new MouseEvent('click'))
            return;
          }
        }
        window.alert(`Result with tag "${tag}" not found`);
      },
      isTypingInEditableField(event) {
        const target = event.target
        if (!target) return false
        const tagName = target.tagName
        return tagName === 'INPUT' || tagName === 'TEXTAREA' || target.isContentEditable
      },
      isSessionDialogOpen() {
        return !!(
          this.remove_dialog ||
          this.restore_dialog ||
          this.permanent_delete_dialog ||
          this.trial_rename_dialog ||
          this.session_rename_dialog ||
          this.trial_modify_tags ||
          this.showAnalysisDialog ||
          this.new_session_confirm_dialog ||
          this.new_session_same_setup_confirm_dialog ||
          this.dialog ||
          this.showArchiveDialog ||
          this.showTrialMenuSheet
        )
      },
      isDeleteShortcut(event) {
        return event.key === 'Backspace' || event.key === 'Delete'
      },
      isPermanentDeleteShortcut(event) {
        return this.isDeleteShortcut(event) && (event.metaKey || event.ctrlKey)
      },
      isTrialNavigationShortcut(event) {
        return !event.metaKey && !event.ctrlKey && !event.altKey && ['ArrowUp', 'ArrowDown'].includes(event.key)
      },
      openSelectedTrialTrashDialog() {
        const selectedTrial = this.selectedTrialFromList
        if (!selectedTrial || selectedTrial.trashed) return false

        this.trialForTrashDialog = selectedTrial
        this.remove_dialog = true
        return true
      },
      openSelectedTrialPermanentDeleteDialog() {
        const selectedTrial = this.selectedTrialFromList
        if (!selectedTrial) return false

        this.trialForPermanentDeleteDialog = selectedTrial
        this.permanent_delete_dialog = true
        return true
      },
      navigateSelectedTrial(direction) {
        if (this.trialLoading) return false

        const trials = this.filteredTrials
        if (trials.length === 0) return false

        const selectedTrial = this.selectedTrialFromList
        if (!selectedTrial) {
          if (direction > 0) {
            this.loadTrial(trials[0])
            return true
          }
          return false
        }

        const currentIndex = trials.findIndex(trial => trial.id === selectedTrial.id)
        if (currentIndex < 0) {
          if (direction > 0) {
            this.loadTrial(trials[0])
            return true
          }
          return false
        }

        const nextIndex = currentIndex + direction
        if (nextIndex < 0 || nextIndex >= trials.length) return true

        this.loadTrial(trials[nextIndex])
        return true
      },
      handleTrialListShortcut(event) {
        if (
          event.defaultPrevented ||
          this.isTypingInEditableField(event) ||
          this.isSessionDialogOpen()
        ) {
          return
        }

        if (this.isDeleteShortcut(event)) {
          const openedDialog = this.isPermanentDeleteShortcut(event)
            ? this.openSelectedTrialPermanentDeleteDialog()
            : this.openSelectedTrialTrashDialog()

          if (openedDialog) {
            event.preventDefault()
          }
          return
        }

        if (this.isTrialNavigationShortcut(event)) {
          const handled = this.navigateSelectedTrial(event.key === 'ArrowUp' ? -1 : 1)
          if (handled) {
            event.preventDefault()
          }
        }
      },
      handleKeyboard: debounce(function(event) {
        if (event.defaultPrevented) {
          return
        }

        // Ignore if user is typing in an input field
        if (this.isTypingInEditableField(event)) {
          return
        }

        if (this.isSessionDialogOpen() && this.isTrialNavigationShortcut(event)) {
          return
        }

        if (this.isTrialNavigationShortcut(event)) {
          return
        }

        // Only handle playback keyboard events when trial video controls are enabled
        if (this.videoControlsDisabled) {
          return
        }

        switch (event.key) {
          case ' ': // Space bar - toggle play/pause
            event.preventDefault()
            this.togglePlay(!this.playing)
            break
          case 'ArrowLeft': // Left arrow - previous frame
            event.preventDefault()
            if (this.frame > 0) {
              this.onNavigate(this.frame - 1)
            }
            break
          case 'ArrowRight': // Right arrow - next frame
            event.preventDefault()
            if (this.frame < this.frames.length - 1) {
              this.onNavigate(this.frame + 1)
            }
            break
        }
      })
    }
  }
  </script>
  
  <style lang="scss">
  .trashed {
    color: gray !important;
  }

  /* Trial context menu - prevent overflow on small screens */
  .trial-context-menu {
    max-width: min(320px, calc(100vw - 24px));
  }

  /* Trial color legend popover */
  .trial-legend-wrap {
    position: relative;
    z-index: 6;
    flex-shrink: 0;
  }
  .trial-legend {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 168px;
    padding: 10px 12px;
    font-size: 12px;
    color: var(--app-text-primary);
    background-color: var(--app-surface-opaque);
    border: 1px solid var(--app-border-strong);
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 8px;
      margin-bottom: 2px;
    }

    &__title {
      font-weight: 600;
      font-size: 11px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      opacity: 0.7;
    }

    &__close {
      margin: 0 -6px 0 0 !important;
    }

    &__item {
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
    }

    &__dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      margin-right: 8px;
      flex-shrink: 0;

      &--done { background-color: green; }
      &--processing { background-color: orange; }
      &--error { background-color: red; }
      &--local { background-color: #9c27b0; }
    }
  }

  /* Analysis submenu - adequate touch targets and width on mobile */
  .analysis-submenu {
    min-width: 180px;
    max-width: min(260px, calc(100vw - 32px));
  }
  .analysis-submenu-list .v-list-item {
    min-height: 48px;
  }
  .analysis-menu-btn {
    min-width: 48px !important;
    min-height: 48px !important;
  }

  /* Trial menu bottom sheet - safe area for notched phones */
  .trial-menu-sheet {
    padding-bottom: env(safe-area-inset-bottom, 0);
    background-color: var(--app-surface-opaque) !important;
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    overflow: hidden;
  }
  .trial-menu-sheet .v-list {
    background-color: transparent !important;
  }
  .trial-menu-sheet .v-list-item {
    justify-content: center !important;
  }
  .trial-menu-sheet .v-list-item__content {
    flex: 0 0 auto !important;
    flex-direction: row !important;
  }
  
  .text-orange {
    color: orange !important;
  }

  .ui-no-zoom {
    touch-action: pan-x pan-y;
    -webkit-user-select: none;
    user-select: none;
  }
  .ui-no-zoom input,
  .ui-no-zoom textarea,
  .ui-no-zoom .v-text-field input {
    -webkit-user-select: text;
    user-select: text;
  }
  
  .step-5 {
    position: fixed;
    top: var(--app-bar-top-offset, 64px);
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: row;
    overflow: hidden;
    z-index: 1;
    background-color: var(--app-background);
    
.main-content {
      min-width: 0;
      flex: 1 1 auto;
      height: 100%;
      display: flex;
      flex-direction: row;
      overflow: hidden;
      position: relative;
    }

    .participant-context {
      display: flex;
      align-items: baseline;
      gap: 6px;
      border: 1px solid var(--app-border-strong);
      border-radius: 6px;
      padding: 6px 12px;
      background-color: var(--app-surface-muted);
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.28);
      color: var(--app-text-primary);
      max-width: 100%;
      min-width: 0;
      overflow: hidden;
    }

    .participant-context__label {
      color: var(--app-text-muted);
      font-size: 0.85rem;
      font-weight: 600;
      line-height: 1.25;
      flex-shrink: 0;
    }

    .participant-context__name {
      font-size: 0.95rem;
      font-weight: 600;
      line-height: 1.25;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  
    .mobile-menu-toggle {
      position: fixed;
      top: calc(var(--app-bar-top-offset, 56px) + 8px);
      left: 8px;
      z-index: 100;
      background-color: var(--app-toolbar-action-bg) !important;
      color: var(--app-toolbar-action-text) !important;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
      border-radius: 8px !important;
      min-width: 48px !important;
      width: 48px !important;
      min-height: 48px !important;
      height: 48px !important;

      .v-icon {
        font-size: 28px !important;
      }

      @media (min-width: 1280px) {
        display: none !important;
      }
    }
    
    .mobile-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 98;
      
      @media (min-width: 1280px) {
        display: none !important;
      }
    }
    
    .left-wrapper {
      min-width: 0;
      flex-shrink: 0;
      position: relative;
      z-index: 99;
      display: flex;
    }

    .left-wrapper.mobile-drawer {
      position: fixed;
      top: var(--app-bar-top-offset, 56px);
      left: 0;
      bottom: 0;
      width: 280px;
      max-width: 85vw;
      transform: translateX(-100%);
      transition: transform 0.3s ease;
      overflow: visible;

      &.mobile-open {
        transform: translateX(0);
      }
    }

    .left {
      min-width: 0;
      min-height: 0;
      overflow: hidden;
      flex: 1;
      display: flex;
      flex-direction: column;
      width: 250px;
      height: 100%;
      background-color: var(--app-surface-opaque);
      border-right: 1px solid var(--app-border);
    }

    .left-wrapper.mobile-drawer .left {
      width: 100%;
      height: 100%;
      box-shadow: 2px 0 8px rgba(0, 0, 0, 0.3);
      padding-top: 48px;
      background-color: var(--app-surface-opaque);
    }

    .left {
      .left-scroll {
        min-height: 0;
        flex: 1 1 auto;
        overflow-y: auto;
        overflow-x: hidden;
        padding-bottom: 12px;
      }

      .session-actions-toggle {
        margin-top: 8px;
        flex-shrink: 0;
        width: 100% !important;
        min-width: 0;
        box-sizing: border-box;
        height: 36px !important;
        min-height: 36px !important;

        @media (max-width: 599px) {
          height: 44px !important;
          min-height: 44px !important;
        }
      }

      .session-actions-panel {
        flex-shrink: 0;
        overflow-y: auto;
        overflow-x: hidden;
        margin-top: 8px;
      }

      .trials-wrapper {
        min-height: 0;
        overflow: visible;
      }

      .trial-name-row {
        gap: 8px;
      }

      .show-removed-trials-sidebar {
        flex-shrink: 0;
        position: relative;
        z-index: 6;
        overflow: visible;
      }

      .show-removed-trials-sidebar .toolbar-checkbox {
        flex-grow: 0;
      }

      .toolbar-checkbox {
        flex-shrink: 0;

        ::v-deep .v-input {
          margin-top: 0;
          padding-top: 0;
        }
        ::v-deep .v-input__control {
          align-items: center;
        }
        ::v-deep .v-messages {
          display: none;
        }
      }

      .session-action-btn {
        width: 100%;
        height: 36px !important;
        min-height: 36px !important;
        font-size: 0.9375rem !important;

        @media (max-width: 599px) {
          height: 44px !important;
          min-height: 44px !important;
        }
      }

      .trials {
        overflow-y: visible;
        flex-grow: 0;
  
        .trial {
          border-radius: 4px;
          padding: 2px 6px;
  
          &.selected {
            background-color: var(--app-selected);
            cursor: default;
          }
        }
      }
      
    }

    .sidebar-toggle-btn {
      position: absolute;
      top: 50%;
      right: -16px;
      transform: translateY(-50%);
      z-index: 103;
      background-color: var(--app-toolbar-action-bg) !important;
      color: var(--app-toolbar-action-text) !important;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
      min-width: 34px !important;
      max-width: 34px !important;
      width: 34px !important;
      min-height: 34px !important;
      max-height: 34px !important;
      height: 34px !important;
      padding: 0 !important;
      border-radius: 50% !important;
      aspect-ratio: 1;

      .v-icon {
        font-size: 22px !important;
      }

      @media (min-width: 1280px) {
        display: none !important;
      }
    }
  
    .viewer {
      flex: 1 1 auto;
      height: 100%;
      min-height: 0;
      min-width: 0;
      display: flex;
      flex-direction: column;
      overflow: hidden;
  
      .mocap-wrapper {
        position: relative;
        min-height: 0;
      }

      .mocap-inner {
        width: 100%;
        height: 100%;
      }

      .mocap-loading-overlay {
        position: absolute;
        inset: 0;
        background-color: var(--app-background);
        z-index: 5;
      }

      #mocap {
        width: 100%;
        height: 100%;
        min-height: 0;
        overflow: hidden;
        flex: 1 1 auto;
        touch-action: none;
  
        canvas {
          display: block;
          width: 100% !important;
          height: 100% !important;
        }
      }

      .session-empty-state {
        flex: 1 1 auto;
        min-height: 0;
        color: var(--app-text-primary);

        h3 {
          font-size: 1.4rem;
          font-weight: 500;
        }

        p {
          max-width: 420px;
          color: var(--app-text-muted);
        }
      }
    }
  
    .right {
      flex-shrink: 0;
      min-width: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;

      .mobile-video-toolbar {
        padding: 0;
        margin-top: 8px;
      }

      .cam-size-overlay {
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 10;
        background-color: rgba(0, 0, 0, 0.65) !important;
        backdrop-filter: blur(4px);
        border: 1px solid rgba(255, 255, 255, 0.25) !important;
        border-radius: 6px !important;
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
        overflow: hidden;

        /* Vuetify btn-toggle resets */
        height: auto !important;
        min-height: unset !important;

        .cam-size-segment {
          min-width: 26px !important;
          width: 26px !important;
          height: 22px !important;
          min-height: unset !important;
          font-size: 0.65rem !important;
          font-weight: 600 !important;
          letter-spacing: 0.02em;
          text-transform: none !important;
          padding: 0 !important;
          color: rgba(255, 255, 255, 0.6) !important;
          background-color: transparent !important;
          border-radius: 0 !important;
          border: none !important;

          &.v-btn--active {
            color: #fff !important;
            background-color: rgba(255, 255, 255, 0.2) !important;
          }

          &:not(:last-child) {
            border-right: 1px solid rgba(255, 255, 255, 0.2) !important;
          }
        }
      }
  
      @media (max-width: 959px) {
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        width: 120px;
        min-width: 100px;
        max-width: 35%;
        height: auto;
        z-index: 1;
      }
  
      @media (min-width: 960px) {
        position: absolute;
        right: 0;
        top: 0;
        bottom: var(--video-controls-height, 90px);
        flex: none;
        width: 200px;
        max-height: none;
        height: auto;
        z-index: 1;
      }
  
      .videos {
        overflow-y: auto;
        overflow-x: hidden;
        width: 100%;
        display: flex;
        flex-direction: column;
        padding: 0;
        margin: 0;
        min-height: 0;
        flex: 1 1 0;
        
        @media (min-width: 960px) {
          width: 100%;
          padding: 0;
          margin: 0;
        }

        @media (max-width: 959px) {
          align-items: flex-end;
          padding-bottom: 120px;
        }
      }

      .right-spacer {
        flex: 1 1 auto;
        min-height: 0;
        pointer-events: none;

        @media (max-width: 959px) {
          flex: 0 0 0;
          min-height: 0;
          overflow: hidden;
        }

        @media (min-width: 960px) {
          flex: 0 0 0;
          background: transparent;
        }
      }
      
      .video-element {
        width: 100%;
        height: auto;
        object-fit: contain;
        background-color: transparent;
        margin: 0;
        padding: 0;
        display: block;
        
        @media (max-width: 959px) {
          width: 100%;
          object-position: right center;
          max-height: var(--mobile-video-max-height, 120px);
        }
        
        @media (min-width: 960px) {
          max-height: none;
        }
      }
      
      .playback-controls {
        flex-shrink: 0;
        padding: 6px 8px;
        background-color: var(--app-surface-muted);
        border-top: 1px solid var(--app-border);

        .playback-controls-row {
          display: flex;
          align-items: center;
          flex-wrap: nowrap;
          gap: 2px;
          min-height: 40px;
        }

        .camera-view-controls {
          flex: 0 0 auto;

          .v-btn {
            width: 32px;
            height: 32px;
          }

          .v-icon {
            font-size: 18px !important;
          }
        }

        .playback-navigation {
          flex: 1 1 auto;
          min-width: 0;
          justify-content: space-evenly !important;

          .v-btn {
            width: 32px;
            height: 32px;
          }

          .v-icon {
            font-size: 18px !important;
          }
        }

        .playback-speed {
          flex: 0 0 auto;

          .speed-control-button {
            min-width: 44px;
            height: 32px;
            padding: 0 6px;
            font-size: 0.75rem;
          }
        }

        .playback-timeline-mobile {
          margin: 0;
          min-height: 0;
          height: 36px;

          .time-input {
            flex: 0 0 64px !important;
            width: 64px !important;
            max-width: 64px !important;
            min-width: 64px !important;
            margin-top: 0 !important;
            margin-bottom: 0 !important;
            padding-top: 0 !important;

            .v-input__control {
              min-height: 32px !important;
            }

            .v-input__slot {
              margin-bottom: 0 !important;
              min-height: 32px !important;
            }

            .v-label {
              font-size: 11px;
            }

            input {
              font-size: 13px;
              padding: 0 !important;
            }
          }

          .timeline-slider {
            flex: 1 1 auto;
            min-width: 0;
            margin: 0 !important;

            .v-input__control,
            .v-input__slot {
              min-height: 32px !important;
              margin: 0 !important;
            }
          }
        }

        .playback-video-size {
          flex: 0 0 auto;
          text-transform: none;
          min-width: 70px;
          margin-right: 4px;
        }
        
        @media (max-width: 959px) {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 50;
          background-color: var(--bottom-toolbar-bg);
          border-top: 1px solid var(--app-border-strong);
          padding: 6px 6px 4px;
          padding-bottom: calc(6px + env(safe-area-inset-bottom, 0px));
        }
      }
    }

    .video-controls {
      width: 100%;
      gap: 8px;
      position: relative;
      z-index: 2;
      flex-shrink: 0;
      min-height: 86px;

      @media (max-width: 959px) {
        /* Hide desktop controls on mobile - they duplicate the mobile fixed bar */
        display: none !important;
      }

      @media (min-width: 960px) {
        gap: 12px;
      }

      @media (max-width: 599px) {
        flex-wrap: nowrap;
      }

      .timeline-slider {
        min-width: 140px;
      }
      
      .time-input {
        min-width: 100px;
        max-width: 150px;
        margin-right: 8px;
        
        @media (max-width: 599px) {
          min-width: 0;
          width: 48px;
          max-width: 48px;
          margin-right: 8px;
          margin-bottom: 0;
        }
      }

      .playback-controls-inline {
        flex: 0 0 auto;
        min-width: max-content;
      }

      @media (min-width: 960px) {
        .timeline-slider {
          margin-bottom: 0 !important;
        }

        .time-input {
          margin-right: 0;
          min-width: 108px;
          max-width: 130px;
        }
      }
    }

    .open-in-app-center {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      z-index: 10;
      padding: 24px;
      /* Let clicks pass through the overlay except for the explicit button */
      pointer-events: none;

      h2 {
        font-size: 1.75rem;
        font-weight: 500;
      }

      p {
        font-size: 1rem;
        opacity: 0.9;
        max-width: 400px;
      }

      .open-in-app-btn {
        font-size: 1.1rem;
        padding: 16px 32px;
        height: auto;
        /* Make only the button clickable */
        pointer-events: auto;
      }

      .open-in-app-store-link {
        color: #ffcc80;
        text-decoration: underline;
        text-underline-offset: 2px;
        pointer-events: auto;
      }
    }
  }

  .monocular-beta-alert {
    font-size: 0.85rem;
  }
  .monocular-beta-alert a {
    color: #64b5f6;
    text-decoration: underline;
  }
  .monocular-beta-alert a:hover {
    color: #90caf9;
  }
  </style>
